// ✅ FILE: src/pages/financials/FinancialPackEditor.jsx
// Replace the whole file with this (keeps all features, fixes placeholders, directors page defaults,
// updates tax (CIT + Dev Levy only), adds capital allowance computation from fixed assets,
// makes Word/Preview formatted + consistent, Word: logo on cover only, PDF: logo on all pages).

import React, { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../Services/supabaseClient";
import { useParams } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./packs.css";

/* ------------------------ helpers ------------------------ */

function latestVersion(versions = []) {
  return [...versions].sort((a, b) => b.version_no - a.version_no)[0] || null;
}

function stripHtml(html) {
  return String(html || "")
    .replace(/<[^>]*>?/gm, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function minusOneDay(yyyyMmDd) {
  if (!yyyyMmDd) return "";
  const d = new Date(`${yyyyMmDd}T00:00:00`);
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

function shiftYear(iso, years = -1) {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  d.setFullYear(d.getFullYear() + years);
  return d.toISOString().slice(0, 10);
}

function parseBrandColors(raw) {
  const fallback = {
    primary: "#6f2dbd",
    secondary: "#9b5de5",
    text: "#111827",
    muted: "#6b7280",
  };

  if (!raw) return fallback;

  try {
    const obj = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!obj || typeof obj !== "object") return fallback;

    return {
      primary: obj.primary || obj.brandPrimary || fallback.primary,
      secondary: obj.secondary || obj.brandSecondary || fallback.secondary,
      text: obj.text || fallback.text,
      muted: obj.muted || fallback.muted,
    };
  } catch {
    return fallback;
  }
}

async function loadImageAsDataUrl(url) {
  if (!url) return null;
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function hexToRgb(hex) {
  const h = String(hex || "")
    .replace("#", "")
    .trim();
  if (h.length !== 6) return [111, 45, 189];
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return [r, g, b];
}

function fmtMoney(n) {
  return Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtMoneyOr0(n) {
  const x = Number(n || 0);
  if (!Number.isFinite(x)) return "0.00";
  return fmtMoney(x);
}

/**
 * Replace placeholders in HTML with computed/company values.
 * Handles {{key}} with whitespace.
 */
function replacePlaceholders(html, vars) {
  let out = String(html || "");
  for (const [k, v] of Object.entries(vars || {})) {
    const safeVal = v === null || v === undefined ? "" : String(v);
    const re = new RegExp(`{{\\s*${k}\\s*}}`, "g");
    out = out.replace(re, safeVal);
  }
  return out;
}

/**
 * ✅ FINAL CLEANUP: remove ANY remaining {{...}} placeholders
 * so the pack never shows raw placeholders anywhere (Preview/Word).
 *
 * Default is "0" (cleaner than blank). If you prefer blank, change defaultText to "".
 */
function removeAnyRemainingPlaceholders(html, defaultText = "0") {
  return String(html || "").replace(/{{\s*[^}]+\s*}}/g, defaultText);
}

/* ------------------------ Fixed Assets Rollforward helpers ------------------------ */

const toDate = (iso) => new Date(`${iso}T00:00:00Z`);
const isBefore = (a, b) => toDate(a).getTime() < toDate(b).getTime();
const isOnOrBefore = (a, b) => toDate(a).getTime() <= toDate(b).getTime();
const isBetweenInc = (x, s, e) => {
  const t = toDate(x).getTime();
  return t >= toDate(s).getTime() && t <= toDate(e).getTime();
};

function computeFARollForward(raw, startISO, endISO) {
  const assets = raw?.assets || [];
  const disposals = raw?.disposals || [];
  const depLines = raw?.depLines || [];

  const disposalByAsset = new Map();
  for (const d of disposals) {
    if (!d.disposal_date) continue;
    const prev = disposalByAsset.get(d.asset_id);
    if (!prev) disposalByAsset.set(d.asset_id, d);
    else if (
      toDate(d.disposal_date).getTime() < toDate(prev.disposal_date).getTime()
    )
      disposalByAsset.set(d.asset_id, d);
  }

  const depLinesByAsset = new Map();
  for (const l of depLines) {
    if (!l.posting_date) continue;
    const arr = depLinesByAsset.get(l.asset_id) || [];
    arr.push(l);
    depLinesByAsset.set(l.asset_id, arr);
  }
  for (const [id, arr] of depLinesByAsset.entries()) {
    arr.sort(
      (x, y) =>
        toDate(x.posting_date).getTime() - toDate(y.posting_date).getTime()
    );
    depLinesByAsset.set(id, arr);
  }

  const sumDepThrough = (assetId, throughISO) => {
    const arr = depLinesByAsset.get(assetId) || [];
    let s = 0;
    for (const l of arr) {
      if (isOnOrBefore(l.posting_date, throughISO))
        s += Number(l.depreciation_amount || 0);
    }
    return s;
  };

  const openingAssets = [];
  const additionsAssets = [];
  const disposalsInPeriod = [];
  const closingAssets = [];

  for (const a of assets) {
    if (!a.purchase_date) continue;
    const disp = disposalByAsset.get(a.id);
    const dispDate = disp?.disposal_date || null;

    const disposedBeforeStart = dispDate ? isBefore(dispDate, startISO) : false;
    const disposedInRange = dispDate
      ? isBetweenInc(dispDate, startISO, endISO)
      : false;
    const disposedByEnd = dispDate ? isOnOrBefore(dispDate, endISO) : false;

    if (isBefore(a.purchase_date, startISO) && !disposedBeforeStart)
      openingAssets.push(a);
    if (isBetweenInc(a.purchase_date, startISO, endISO))
      additionsAssets.push(a);
    if (disposedInRange) disposalsInPeriod.push({ asset: a, disposal: disp });
    if (isOnOrBefore(a.purchase_date, endISO) && !disposedByEnd)
      closingAssets.push(a);
  }

  const sumCost = (arr) => arr.reduce((s, a) => s + Number(a.cost || 0), 0);

  const openingCost = sumCost(openingAssets);
  const additionsCost = sumCost(additionsAssets);
  const disposalsCost = disposalsInPeriod.reduce(
    (s, x) => s + Number(x.asset.cost || 0),
    0
  );
  const closingCost = sumCost(closingAssets);

  const openingAccumDep = openingAssets.reduce(
    (s, a) => s + sumDepThrough(a.id, minusOneDay(startISO)),
    0
  );

  const depThisPeriod = Array.from(
    new Set(
      [
        ...openingAssets,
        ...additionsAssets,
        ...closingAssets,
        ...disposalsInPeriod.map((x) => x.asset),
      ].map((a) => a.id)
    )
  ).reduce((s, aid) => {
    const arr = depLinesByAsset.get(aid) || [];
    for (const l of arr) {
      if (isBetweenInc(l.posting_date, startISO, endISO))
        s += Number(l.depreciation_amount || 0);
    }
    return s;
  }, 0);

  const accumDepOnDisposals = disposalsInPeriod.reduce((s, x) => {
    const dispDate = x.disposal?.disposal_date;
    if (!dispDate) return s;
    return s + sumDepThrough(x.asset.id, dispDate);
  }, 0);

  const closingAccumDep = openingAccumDep + depThisPeriod - accumDepOnDisposals;
  const closingNBV = closingCost - closingAccumDep;

  // by category closing
  const byCategory = new Map();
  for (const a of closingAssets) {
    const cat = a.category || "Uncategorized";
    const depToEnd = sumDepThrough(a.id, endISO);
    const nbv = Number(a.cost || 0) - depToEnd;
    const prev = byCategory.get(cat) || { cost: 0, accum: 0, nbv: 0 };
    prev.cost += Number(a.cost || 0);
    prev.accum += depToEnd;
    prev.nbv += nbv;
    byCategory.set(cat, prev);
  }
  const byCategoryRows = Array.from(byCategory.entries())
    .map(([category, v]) => ({
      category,
      cost: v.cost,
      accum: v.accum,
      nbv: v.nbv,
    }))
    .sort((a, b) => b.nbv - a.nbv);

  return {
    openingCost,
    additionsCost,
    disposalsCost,
    closingCost,
    openingAccumDep,
    depThisPeriod,
    accumDepOnDisposals,
    closingAccumDep,
    closingNBV,
    byCategoryRows,
  };
}

/* ------------------------ Capital allowance (NEW) ------------------------ */
/**
 * Computes a conservative capital allowance estimate using fixed assets data.
 * - Uses a simple category->rate mapping (editable).
 * - IA applies to additions in period only (where applicable).
 * - AA applies to qualifying base (opening + additions - disposals) for the period.
 *
 * If you later store tax classes/rates per asset, swap mapping logic here.
 */
function normalizeCat(s) {
  return String(s || "").toLowerCase();
}

// You can tune these mappings anytime.
function getTaxClassFromCategory(cat) {
  const c = normalizeCat(cat);

  if (/(land)/i.test(c)) return "Land"; // typically non-qualifying
  if (/(building|factory|warehouse|office)/i.test(c)) return "Building";
  if (/(furniture|fitting)/i.test(c)) return "Furniture";
  if (/(motor|vehicle|car|truck|van)/i.test(c)) return "MotorVehicle";
  if (/(computer|laptop|server|it|software)/i.test(c)) return "Computer";
  if (/(plant|machine|machinery|equipment|tool)/i.test(c))
    return "PlantMachinery";
  if (/(fixture)/i.test(c)) return "Furniture";
  return "PlantMachinery";
}

/**
 * Rates are expressed as:
 *  - ia: initial allowance rate
 *  - aa: annual allowance rate
 *  - qualify: whether it qualifies at all
 */
function capAllowRates(taxClass) {
  // Reasonable defaults; adjust as needed for your practice.
  // NOTE: Land treated as non-qualifying here.
  const map = {
    Land: { qualify: false, ia: 0, aa: 0 },
    Building: { qualify: true, ia: 0.15, aa: 0.1 },
    Furniture: { qualify: true, ia: 0.25, aa: 0.2 },
    MotorVehicle: { qualify: true, ia: 0.5, aa: 0.25 },
    Computer: { qualify: true, ia: 0.5, aa: 0.25 },
    PlantMachinery: { qualify: true, ia: 0.5, aa: 0.25 },
  };
  return map[taxClass] || map.PlantMachinery;
}

function computeCapitalAllowanceFromFA(faRaw, startISO, endISO) {
  if (!faRaw || !startISO || !endISO) return 0;

  const rf = computeFARollForward(faRaw, startISO, endISO);

  // Build opening/additions/disposals buckets per category
  const assets = faRaw?.assets || [];
  const disposals = faRaw?.disposals || [];

  const disposalByAsset = new Map();
  for (const d of disposals) {
    if (!d.disposal_date) continue;
    const prev = disposalByAsset.get(d.asset_id);
    if (!prev) disposalByAsset.set(d.asset_id, d);
    else if (
      toDate(d.disposal_date).getTime() < toDate(prev.disposal_date).getTime()
    )
      disposalByAsset.set(d.asset_id, d);
  }

  const openingByCat = new Map();
  const additionsByCat = new Map();
  const disposalsByCat = new Map();

  for (const a of assets) {
    if (!a.purchase_date) continue;
    const cat = a.category || "Uncategorized";
    const disp = disposalByAsset.get(a.id);
    const dispDate = disp?.disposal_date || null;

    const disposedBeforeStart = dispDate ? isBefore(dispDate, startISO) : false;
    const disposedInRange = dispDate
      ? isBetweenInc(dispDate, startISO, endISO)
      : false;
    const disposedByEnd = dispDate ? isOnOrBefore(dispDate, endISO) : false;

    const cost = Number(a.cost || 0);

    // opening: purchased before start, not disposed before start
    if (isBefore(a.purchase_date, startISO) && !disposedBeforeStart) {
      openingByCat.set(cat, (openingByCat.get(cat) || 0) + cost);
    }

    // additions in range
    if (isBetweenInc(a.purchase_date, startISO, endISO)) {
      additionsByCat.set(cat, (additionsByCat.get(cat) || 0) + cost);
    }

    // disposals in range (cost)
    if (disposedInRange) {
      disposalsByCat.set(cat, (disposalsByCat.get(cat) || 0) + cost);
    }

    // note: closing is in rf; we only need base for AA
    void disposedByEnd;
  }

  let totalAllowance = 0;

  const cats = new Set([
    ...Array.from(openingByCat.keys()),
    ...Array.from(additionsByCat.keys()),
    ...Array.from(disposalsByCat.keys()),
  ]);

  for (const cat of cats) {
    const taxClass = getTaxClassFromCategory(cat);
    const rates = capAllowRates(taxClass);
    if (!rates.qualify) continue;

    const opening = openingByCat.get(cat) || 0;
    const additions = additionsByCat.get(cat) || 0;
    const disposalsCost = disposalsByCat.get(cat) || 0;

    // Qualifying base for AA (simple approach)
    const base = Math.max(0, opening + additions - disposalsCost);

    const ia = additions * rates.ia;
    const aa = base * rates.aa;

    totalAllowance += ia + aa;
  }

  // Guard: allowance cannot be negative
  totalAllowance = Math.max(0, totalAllowance);

  // rf is computed above to ensure consistent date logic; keep it used
  void rf;

  return totalAllowance;
}

/* ------------------------ Notes helpers: PL + SOFP breakdown ------------------------ */

function plToHeads(rows = []) {
  const map = new Map();
  for (const r of rows) {
    const key = `${r.account_type}||${r.account_name}`;
    const dr = Number(r.total_debit) || 0;
    const cr = Number(r.total_credit) || 0;
    let amt = 0;
    if (r.account_type === "Revenue") amt = cr - dr;
    if (r.account_type === "Cost of Sales") amt = dr - cr;
    if (r.account_type === "Expense") amt = dr - cr;
    map.set(key, { type: r.account_type, name: r.account_name, amt });
  }
  return Array.from(map.values());
}

function mergePLHeads(headsCur, headsPrev, type) {
  const cur = (headsCur || []).filter((x) => x.type === type);
  const prev = (headsPrev || []).filter((x) => x.type === type);

  const byName = new Map();
  for (const x of prev)
    byName.set(String(x.name), { name: x.name, prev: x.amt, cur: 0 });
  for (const x of cur) {
    const e = byName.get(String(x.name)) || { name: x.name, prev: 0, cur: 0 };
    e.cur = x.amt;
    byName.set(String(x.name), e);
  }

  return Array.from(byName.values())
    .filter((x) => Math.abs(x.cur) > 0.0001 || Math.abs(x.prev) > 0.0001)
    .sort((a, b) => Math.abs(b.cur) - Math.abs(a.cur));
}

function sofpBuckets(rows = []) {
  const all = rows.map((r) => ({
    name: r.account_name,
    type: r.account_type,
    debit: Number(r.total_debit) || 0,
    credit: Number(r.total_credit) || 0,
  }));

  const assets = [];
  const liabilities = [];
  const equity = [];

  for (const a of all) {
    let bal = 0;
    // Assets debit-normal; Liab/Equity credit-normal
    if (a.type === "Asset") bal = a.debit - a.credit;
    else bal = a.credit - a.debit;

    if (a.type === "Asset") assets.push({ name: a.name, bal });
    if (a.type === "Liability") liabilities.push({ name: a.name, bal });
    if (a.type === "Equity") equity.push({ name: a.name, bal });
  }

  const sumBal = (arr) => arr.reduce((s, x) => s + (Number(x.bal) || 0), 0);

  return {
    assets,
    liabilities,
    equity,
    totals: {
      assets: sumBal(assets),
      liabilities: sumBal(liabilities),
      equity: sumBal(equity),
    },
  };
}

function mergeSOFPByName(curArr, prevArr) {
  const m = new Map();
  for (const x of prevArr) m.set(x.name, { name: x.name, cur: 0, prev: x.bal });
  for (const x of curArr) {
    const e = m.get(x.name) || { name: x.name, cur: 0, prev: 0 };
    e.cur = x.bal;
    m.set(x.name, e);
  }
  return Array.from(m.values()).filter(
    (x) => Math.abs(x.cur) > 0.0001 || Math.abs(x.prev) > 0.0001
  );
}

/* ------------------------ Value Added helpers (kept) ------------------------ */

function pickByRegex(rows, re) {
  const hits = (rows || []).filter((r) =>
    re.test(String(r.account_name || ""))
  );
  return hits.reduce((s, r) => {
    const dr = Number(r.total_debit) || 0;
    const cr = Number(r.total_credit) || 0;
    // expense debit-normal
    const amt = dr - cr;
    return s + amt;
  }, 0);
}

/* ------------------------ ✅ TAX: NEW RULES (CIT + Dev Levy only) ------------------------ */
/**
 * Per your instruction:
 * - CIT = 0 if revenue < 100m, else 30%
 * - Development levy = 4% of taxable profit
 */
function citRateNew(turnover) {
  const t = Number(turnover || 0);
  if (t < 100000000) return 0.0;
  return 0.3;
}
function devLevyRate() {
  return 0.04;
}

/* ------------------------ Template Vars (placeholders) ------------------------ */
/**
 * IMPORTANT: includes aliases like {{pl_revenue}} + adds missing ones you referenced:
 * - pl_gross, pbt, pl_net, *_prior
 * - retained earnings placeholders default to 0 (unless you later wire equity movements)
 */
function getTemplateVars({ pack, companyInfo, computed, taxComputed }) {
  const companyName = companyInfo?.company_name || "";
  const address = companyInfo?.address || "";
  const tin = companyInfo?.TIN || "";
  const rc = companyInfo?.rc_number || "";
  const incDate = companyInfo?.incorporation_date || "";
  const activities = companyInfo?.business_activities || "";

  const startDate = computed?.period?.startDate || pack?.start_date || "";
  const endDate = computed?.period?.endDate || pack?.end_date || "";
  const priorStart = computed?.period?.priorStart || "";
  const priorEnd = computed?.period?.priorEnd || "";

  const plCur = computed?.pl?.totalsCur || {
    revenue: 0,
    cos: 0,
    expense: 0,
    gross: 0,
    net: 0,
  };
  const plPrev = computed?.pl?.totalsPrev || {
    revenue: 0,
    cos: 0,
    expense: 0,
    gross: 0,
    net: 0,
  };

  const cashCur = computed?.cashflow?.cur || { totalCF: 0 };
  const cashPrev = computed?.cashflow?.prev || { totalCF: 0 };

  const taxCur = taxComputed?.cur || {};
  const taxPrev = taxComputed?.prev || {};

  // ✅ use safe defaults everywhere
  const vars = {
    company_name: companyName,
    registered_address: address,
    tin: tin,
    rc_number: rc,
    incorporation_date: incDate,
    business_activities: activities,

    period_start: startDate,
    period_end: endDate,
    prior_period_start: priorStart,
    prior_period_end: priorEnd,

    currency_code: pack?.currency_code || "NGN",

    // canonical (money)
    revenue_cur: `₦${fmtMoneyOr0(plCur.revenue)}`,
    revenue_prev: `₦${fmtMoneyOr0(plPrev.revenue)}`,
    cos_cur: `₦${fmtMoneyOr0(plCur.cos)}`,
    cos_prev: `₦${fmtMoneyOr0(plPrev.cos)}`,
    gross_profit_cur: `₦${fmtMoneyOr0(plCur.gross)}`,
    gross_profit_prev: `₦${fmtMoneyOr0(plPrev.gross)}`,
    net_profit_cur: `₦${fmtMoneyOr0(plCur.net)}`,
    net_profit_prev: `₦${fmtMoneyOr0(plPrev.net)}`,
    net_cashflow_cur: `₦${fmtMoneyOr0(cashCur.totalCF)}`,
    net_cashflow_prev: `₦${fmtMoneyOr0(cashPrev.totalCF)}`,

    // ✅ aliases you already use
    pl_revenue: `₦${fmtMoneyOr0(plCur.revenue)}`,
    pl_revenue_cur: `₦${fmtMoneyOr0(plCur.revenue)}`,
    pl_revenue_prev: `₦${fmtMoneyOr0(plPrev.revenue)}`,
    pl_revenue_prior: `₦${fmtMoneyOr0(plPrev.revenue)}`, // you referenced this
    pl_cost_of_sales: `₦${fmtMoneyOr0(plCur.cos)}`,
    pl_cos: `₦${fmtMoneyOr0(plCur.cos)}`,
    pl_gross_profit: `₦${fmtMoneyOr0(plCur.gross)}`,
    pl_net_profit: `₦${fmtMoneyOr0(plCur.net)}`,
    pl_net_profit_prev: `₦${fmtMoneyOr0(plPrev.net)}`,
    cashflow_net: `₦${fmtMoneyOr0(cashCur.totalCF)}`,

    // ✅ directors report placeholders you referenced
    pl_gross: `₦${fmtMoneyOr0(plCur.gross)}`,
    pl_gross_prior: `₦${fmtMoneyOr0(plPrev.gross)}`,
    pbt: `₦${fmtMoneyOr0(taxCur.pbt)}`,
    pbt_prior: `₦${fmtMoneyOr0(taxPrev.pbt)}`,
    pl_net: `₦${fmtMoneyOr0(plCur.net)}`,
    pl_net_prior: `₦${fmtMoneyOr0(plPrev.net)}`,

    // ✅ retained earnings extract (defaults to 0 until you wire equity movements)
    re_open: `₦${fmtMoneyOr0(0)}`,
    re_open_prior: `₦${fmtMoneyOr0(0)}`,
    dividend: `₦${fmtMoneyOr0(0)}`,
    dividend_prior: `₦${fmtMoneyOr0(0)}`,
    re_close: `₦${fmtMoneyOr0(0)}`,
    re_close_prior: `₦${fmtMoneyOr0(0)}`,

    // ✅ tax placeholders (if any templates use them)
    tax_capital_allow: `₦${fmtMoneyOr0(taxCur.capitalAllow)}`,
    tax_capital_allow_prior: `₦${fmtMoneyOr0(taxPrev.capitalAllow)}`,
    tax_assessable: `₦${fmtMoneyOr0(taxCur.taxableProfit)}`,
    tax_assessable_prior: `₦${fmtMoneyOr0(taxPrev.taxableProfit)}`,
    tax_cit: `₦${fmtMoneyOr0(taxCur.cit)}`,
    tax_cit_prior: `₦${fmtMoneyOr0(taxPrev.cit)}`,
    tax_dev_levy: `₦${fmtMoneyOr0(taxCur.devLevy)}`,
    tax_dev_levy_prior: `₦${fmtMoneyOr0(taxPrev.devLevy)}`,
    tax_total: `₦${fmtMoneyOr0(taxCur.totalTax)}`,
    tax_total_prior: `₦${fmtMoneyOr0(taxPrev.totalTax)}`,
  };

  return vars;
}

/* ------------------------ HTML builder for Preview + Word ------------------------ */

function htmlEscape(s) {
  return String(s ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

function buildTableHtml({ head = [], rows = [], rightCols = [] }) {
  const th = head.map((h) => `<th>${htmlEscape(h)}</th>`).join("");
  const body = rows
    .map((r) => {
      const tds = r
        .map((cell, idx) => {
          const align = rightCols.includes(idx) ? "text-align:right;" : "";
          return `<td style="${align}">${cell}</td>`;
        })
        .join("");
      return `<tr>${tds}</tr>`;
    })
    .join("");
  return `
    <table>
      <thead><tr>${th}</tr></thead>
      <tbody>${body}</tbody>
    </table>
  `;
}

/**
 * ✅ buildPackHtml supports mode: "preview" | "word"
 * - Word requirement: logo only on first page (cover)
 * - Preview: keep same as Word for consistency
 */
function buildPackHtml({ pack, pages, companyInfo, computed, brand, faRaw }) {
  const included = (pages || []).filter((p) => p.is_included);

  const companyName = companyInfo?.company_name || "Company";
  const logoUrl = companyInfo?.logo_url || "";
  const address = companyInfo?.address || "—";
  const tin = companyInfo?.TIN || "—";
  const rc = companyInfo?.rc_number || "—";
  const incDate = companyInfo?.incorporation_date || "—";
  const activities = companyInfo?.business_activities || "—";

  const primary = brand?.primary || "#6f2dbd";
  const secondary = brand?.secondary || "#9b5de5";

  // Precompute breakdowns for notes and statement rendering
  const headsCur = plToHeads(computed?.pl?.rowsCur || []);
  const headsPrev = plToHeads(computed?.pl?.rowsPrev || []);
  const rowsRev = mergePLHeads(headsCur, headsPrev, "Revenue");
  const rowsCos = mergePLHeads(headsCur, headsPrev, "Cost of Sales");
  const rowsExp = mergePLHeads(headsCur, headsPrev, "Expense");

  const sofpCur = sofpBuckets(computed?.sofp?.endCur || []);
  const sofpPrev = sofpBuckets(computed?.sofp?.endPrev || []);

  const plCur = computed?.pl?.totalsCur || {
    revenue: 0,
    cos: 0,
    expense: 0,
    gross: 0,
    net: 0,
  };
  const plPrev = computed?.pl?.totalsPrev || {
    revenue: 0,
    cos: 0,
    expense: 0,
    gross: 0,
    net: 0,
  };

  // Value Added (kept)
  const wagesCur = pickByRegex(
    computed?.pl?.rowsCur,
    /(salary|wage|staff|payroll)/i
  );
  const wagesPrev = pickByRegex(
    computed?.pl?.rowsPrev,
    /(salary|wage|staff|payroll)/i
  );
  const depCur = pickByRegex(computed?.pl?.rowsCur, /(depreciation)/i);
  const depPrev = pickByRegex(computed?.pl?.rowsPrev, /(depreciation)/i);
  const interestCur = pickByRegex(
    computed?.pl?.rowsCur,
    /(interest|finance cost|loan)/i
  );
  const interestPrev = pickByRegex(
    computed?.pl?.rowsPrev,
    /(interest|finance cost|loan)/i
  );
  const taxExpCur = pickByRegex(computed?.pl?.rowsCur, /(tax)/i);
  const taxExpPrev = pickByRegex(computed?.pl?.rowsPrev, /(tax)/i);

  const valueAddedCur = plCur.revenue - plCur.cos;
  const valueAddedPrev = plPrev.revenue - plPrev.cos;

  // Fixed assets roll-forward for notes (kept)
  const { startDate, endDate, priorStart, priorEnd } = computed?.period || {};
  const faCur =
    faRaw && startDate && endDate
      ? computeFARollForward(faRaw, startDate, endDate)
      : null;
  const faPrev =
    faRaw && priorStart && priorEnd
      ? computeFARollForward(faRaw, priorStart, priorEnd)
      : null;

  // ✅ Capital allowance for tax (NEW)
  const capitalAllowCur =
    faRaw && startDate && endDate
      ? computeCapitalAllowanceFromFA(faRaw, startDate, endDate)
      : 0;
  const capitalAllowPrev =
    faRaw && priorStart && priorEnd
      ? computeCapitalAllowanceFromFA(faRaw, priorStart, priorEnd)
      : 0;

  // ✅ TAX (NEW)
  const turnoverCur = plCur.revenue;
  const turnoverPrev = plPrev.revenue;

  const citRateCur = citRateNew(turnoverCur);
  const citRatePrev = citRateNew(turnoverPrev);

  // pbt: we don’t have tax PBT directly; use net as proxy (kept behaviour)
  const pbtCur = plCur.net;
  const pbtPrev = plPrev.net;

  const disallowableCur = 0;
  const disallowablePrev = 0;

  const taxableProfitCur = Math.max(
    0,
    Number(pbtCur || 0) +
      Number(disallowableCur || 0) -
      Number(capitalAllowCur || 0)
  );
  const taxableProfitPrev = Math.max(
    0,
    Number(pbtPrev || 0) +
      Number(disallowablePrev || 0) -
      Number(capitalAllowPrev || 0)
  );

  const citCur = taxableProfitCur * citRateCur;
  const citPrev = taxableProfitPrev * citRatePrev;

  const devCur = taxableProfitCur * devLevyRate();
  const devPrev = taxableProfitPrev * devLevyRate();

  const totalTaxCur = citCur + devCur;
  const totalTaxPrev = citPrev + devPrev;

  const taxComputed = {
    cur: {
      pbt: pbtCur,
      disallowable: disallowableCur,
      capitalAllow: capitalAllowCur,
      taxableProfit: taxableProfitCur,
      citRate: citRateCur,
      cit: citCur,
      devRate: devLevyRate(),
      devLevy: devCur,
      totalTax: totalTaxCur,
      turnover: turnoverCur,
    },
    prev: {
      pbt: pbtPrev,
      disallowable: disallowablePrev,
      capitalAllow: capitalAllowPrev,
      taxableProfit: taxableProfitPrev,
      citRate: citRatePrev,
      cit: citPrev,
      devRate: devLevyRate(),
      devLevy: devPrev,
      totalTax: totalTaxPrev,
      turnover: turnoverPrev,
    },
  };

  const vars = getTemplateVars({ pack, companyInfo, computed, taxComputed });

  // ✅ Better formatting for Word/Preview
  // - table-layout fixed reduces Word weirdness
  // - prevent logo blow-up by explicit width/height + object-fit
  // - page-break handled by .page
  const css = `
    <style>
      @page { size: A4; margin: 18mm 15mm; }
      html, body { width: 100%; }
      body { font-family: Arial, sans-serif; color: #0f172a; margin: 0; padding: 0; }
      .page { page-break-after: always; padding: 0; position: relative; }
      .topbar { height: 10px; background: ${primary}; width: 100%; }
      .header { display:flex; justify-content:space-between; align-items:flex-start; border-bottom: 2px solid ${primary}; padding: 10px 0; margin: 14px 0 12px; }
      .header h2 { margin:0; font-size: 15px; font-weight: 800; }

      /* ✅ logo sizing (prevents Word blow-up) */
      .logo { width: 62px !important; height: 62px !important; object-fit: contain !important; display:block !important; max-width: 62px !important; max-height: 62px !important; }
      .logoSmall { width: 42px !important; height: 42px !important; object-fit: contain !important; display:block !important; max-width: 42px !important; max-height: 42px !important; }

      .coverWrap { padding: 0; }
      .coverHero { padding: 28px 20px; background: linear-gradient(135deg, ${primary}, ${secondary}); color: #fff; }
      .coverTitle { font-size: 26px; font-weight: 900; margin: 0 0 6px; line-height: 1.15; }
      .coverSub { margin: 0; opacity: .95; font-size: 13px; }
      .coverBody { padding: 16px 20px; }
      .coverCard { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px 14px; background: #ffffff; }
      .muted { color:#475569; font-size: 12px; }
      table { width:100%; border-collapse: collapse; margin-top: 10px; table-layout: fixed; }
      th, td { border: 1px solid #e5e7eb; padding: 8px; font-size: 12px; vertical-align: top; word-wrap: break-word; }
      th { background: ${primary}; color: #fff; text-align:left; }
      .tocTitle { font-size: 16px; font-weight: 900; margin: 6px 0 10px; }
      .hr { height: 1px; background: #e5e7eb; margin: 12px 0; }
      .kpiGrid { display:grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-top: 10px; }
      .kpi { border: 1px solid #e5e7eb; border-radius: 12px; padding: 10px 12px; background: #fff; }
      .kpiLabel { font-size: 11px; color:#64748b; margin-bottom: 6px; }
      .kpiVal { font-size: 14px; font-weight: 800; }
      .small { font-size: 11px; color:#64748b; }
      .sectionTitle { font-weight: 900; font-size: 14px; margin: 6px 0 6px; }
      .subTitle { font-weight: 800; margin-top: 14px; }
      .noteBox { border: 1px solid #e5e7eb; border-radius: 10px; padding: 10px 12px; background: #fff; margin-top: 10px; }

      /* Word-friendly */
      p { margin: 0 0 8px; }
      ul, ol { margin: 0 0 8px 18px; }
    </style>
  `;

  // COVER (logo allowed)
  const cover = `
    <div class="page coverWrap">
      <div class="coverHero">
        <div style="display:flex;justify-content:space-between;gap:14px;align-items:flex-start;">
          <div>
            <div class="coverTitle">${htmlEscape(companyName)}</div>
            <p class="coverSub">AUDIT-FORMAT FINANCIAL STATEMENTS PACK</p>
            <div style="margin-top:10px;font-size:13px;font-weight:700;">
              Period: ${htmlEscape(pack.start_date)} → ${htmlEscape(
    pack.end_date
  )}
            </div>
            <div style="margin-top:4px;font-size:12px;opacity:.95;">
              Comparative: ${htmlEscape(
                computed?.period?.priorStart || ""
              )} → ${htmlEscape(computed?.period?.priorEnd || "")}
            </div>
          </div>
          ${
            logoUrl
              ? `<img class="logoSmall" width="42" height="42" style="width:42px;height:42px;object-fit:contain;" src="${logoUrl}" />`
              : ""
          }
        </div>
      </div>

      <div class="coverBody">
        <div class="coverCard">
          <div style="font-weight:900;margin-bottom:8px;">Corporate Information (Snapshot)</div>
          <table>
            <tbody>
              <tr><td style="width:220px;font-weight:700;">Registered Address</td><td>${htmlEscape(
                address
              )}</td></tr>
              <tr><td style="font-weight:700;">TIN</td><td>${htmlEscape(
                tin
              )}</td></tr>
              <tr><td style="font-weight:700;">RC Number</td><td>${htmlEscape(
                rc
              )}</td></tr>
              <tr><td style="font-weight:700;">Incorporation Date</td><td>${htmlEscape(
                incDate
              )}</td></tr>
              <tr><td style="font-weight:700;">Principal Activities</td><td>${htmlEscape(
                activities
              )}</td></tr>
              <tr><td style="font-weight:700;">Currency</td><td>${htmlEscape(
                pack.currency_code || "NGN"
              )} (Rounded: ${htmlEscape(
    pack.rounding_unit || "naira"
  )})</td></tr>
              <tr><td style="font-weight:700;">Pack Status</td><td>${htmlEscape(
                pack.status
              )}</td></tr>
            </tbody>
          </table>

          <div class="kpiGrid">
            <div class="kpi">
              <div class="kpiLabel">Revenue (Current)</div>
              <div class="kpiVal">₦${fmtMoneyOr0(
                computed?.pl?.totalsCur?.revenue || 0
              )}</div>
              <div class="small">Journal-based</div>
            </div>
            <div class="kpi">
              <div class="kpiLabel">Net Profit/(Loss) (Current)</div>
              <div class="kpiVal">₦${fmtMoneyOr0(
                computed?.pl?.totalsCur?.net || 0
              )}</div>
              <div class="small">Journal-based</div>
            </div>
            <div class="kpi">
              <div class="kpiLabel">Net Cash Flow (Current)</div>
              <div class="kpiVal">₦${fmtMoneyOr0(
                computed?.cashflow?.cur?.totalCF || 0
              )}</div>
              <div class="small">Journal-based</div>
            </div>
          </div>

          <div class="hr"></div>
          <div class="muted">
            This pack is auto-generated from journal entries and includes editable disclosures/narratives.
            Users must review and approve before issuing as final.
          </div>
        </div>
      </div>
    </div>
  `;

  // TOC (✅ no logo on Word/Preview per your requirement)
  const tocRows = included
    .filter((p) => !["cover", "toc"].includes(p.page_key))
    .map(
      (p, idx) =>
        `<tr><td>${htmlEscape(p.title)}</td><td style="text-align:right;">${
          idx + 3
        }</td></tr>`
    )
    .join("");

  const toc = `
    <div class="page">
      <div class="topbar"></div>
      <div class="header">
        <h2>${htmlEscape(companyName)} — Table of Contents</h2>
        <div></div>
      </div>
      <div class="tocTitle">TABLE OF CONTENTS</div>
      <table>
        <thead><tr><th>Section</th><th style="text-align:right;">Page</th></tr></thead>
        <tbody>${tocRows}</tbody>
      </table>
      <div class="muted" style="margin-top:10px;">
        Note: Word pagination may vary depending on local fonts. PDF export uses exact pagination.
      </div>
    </div>
  `;

  const renderComputedPageHtml = (p) => {
    const titleHtml = `
      <div class="topbar"></div>
      <div class="header">
        <h2>${htmlEscape(companyName)} — ${htmlEscape(p.title)}</h2>
        <div></div>
      </div>
      <div class="muted">Current: ${htmlEscape(startDate)} → ${htmlEscape(
      endDate
    )} &nbsp;&nbsp; Comparative: ${htmlEscape(priorStart)} → ${htmlEscape(
      priorEnd
    )}</div>
    `;

    // ✅ Replace placeholders + remove any leftovers (NO {{...}} anywhere)
    const narrativeRaw = p.latest?.content?.html || "";
    const narrativeFilled = removeAnyRemainingPlaceholders(
      replacePlaceholders(narrativeRaw, vars),
      "0"
    );

    // Corporate info page (table + narrative)
    if (p.page_key === "corporate_info") {
      const body = [
        ["Company Name", htmlEscape(companyName)],
        ["Registered Address", htmlEscape(address)],
        ["TIN", htmlEscape(tin)],
        ["RC Number", htmlEscape(rc)],
        ["Incorporation Date", htmlEscape(incDate)],
        ["Principal Activities", htmlEscape(activities)],
        [
          "Reporting Period",
          `${htmlEscape(startDate)} → ${htmlEscape(endDate)}`,
        ],
        [
          "Comparative Period",
          `${htmlEscape(priorStart)} → ${htmlEscape(priorEnd)}`,
        ],
      ];
      const tbl = buildTableHtml({
        head: ["Field", "Value"],
        rows: body.map((r) => [r[0], r[1]]),
        rightCols: [],
      });

      return `${titleHtml}${tbl}${
        narrativeFilled ? `<div class="noteBox">${narrativeFilled}</div>` : ""
      }`;
    }

    // PL page
    if (p.page_key === "pl") {
      const rows = [
        [
          "Revenue",
          `₦${fmtMoneyOr0(plCur.revenue)}`,
          `₦${fmtMoneyOr0(plPrev.revenue)}`,
        ],
        ...rowsRev.map((x) => [
          htmlEscape(x.name),
          `₦${fmtMoneyOr0(x.cur)}`,
          `₦${fmtMoneyOr0(x.prev)}`,
        ]),
        [
          "Cost of Sales",
          `₦${fmtMoneyOr0(plCur.cos)}`,
          `₦${fmtMoneyOr0(plPrev.cos)}`,
        ],
        ...rowsCos.map((x) => [
          htmlEscape(x.name),
          `₦${fmtMoneyOr0(x.cur)}`,
          `₦${fmtMoneyOr0(x.prev)}`,
        ]),
        [
          "Gross Profit",
          `₦${fmtMoneyOr0(plCur.gross)}`,
          `₦${fmtMoneyOr0(plPrev.gross)}`,
        ],
        [
          "Operating Expenses",
          `₦${fmtMoneyOr0(plCur.expense)}`,
          `₦${fmtMoneyOr0(plPrev.expense)}`,
        ],
        ...rowsExp.map((x) => [
          htmlEscape(x.name),
          `₦${fmtMoneyOr0(x.cur)}`,
          `₦${fmtMoneyOr0(x.prev)}`,
        ]),
        [
          plCur.net >= 0 ? "Net Profit" : "Net Loss",
          `₦${fmtMoneyOr0(plCur.net)}`,
          `₦${fmtMoneyOr0(plPrev.net)}`,
        ],
      ];

      const tbl = buildTableHtml({
        head: ["Description", "Current", "Prior"],
        rows,
        rightCols: [1, 2],
      });

      return `${titleHtml}${tbl}${
        narrativeFilled ? `<div class="noteBox">${narrativeFilled}</div>` : ""
      }`;
    }

    // SOFP page
    if (p.page_key === "sofp") {
      const assetsTbl = buildTableHtml({
        head: ["Assets", "Current", "Prior"],
        rows: mergeSOFPByName(sofpCur.assets, sofpPrev.assets).map((x) => [
          htmlEscape(x.name),
          `₦${fmtMoneyOr0(x.cur)}`,
          `₦${fmtMoneyOr0(x.prev)}`,
        ]),
        rightCols: [1, 2],
      });

      const liabTbl = buildTableHtml({
        head: ["Liabilities", "Current", "Prior"],
        rows: mergeSOFPByName(sofpCur.liabilities, sofpPrev.liabilities).map(
          (x) => [
            htmlEscape(x.name),
            `₦${fmtMoneyOr0(x.cur)}`,
            `₦${fmtMoneyOr0(x.prev)}`,
          ]
        ),
        rightCols: [1, 2],
      });

      const eqTbl = buildTableHtml({
        head: ["Equity", "Current", "Prior"],
        rows: mergeSOFPByName(sofpCur.equity, sofpPrev.equity).map((x) => [
          htmlEscape(x.name),
          `₦${fmtMoneyOr0(x.cur)}`,
          `₦${fmtMoneyOr0(x.prev)}`,
        ]),
        rightCols: [1, 2],
      });

      const totals = `
        <div class="noteBox">
          <div><b>Total Assets:</b> ₦${fmtMoneyOr0(
            sofpCur.totals.assets
          )} (Prior: ₦${fmtMoneyOr0(sofpPrev.totals.assets)})</div>
          <div style="margin-top:6px;"><b>Total Liabilities + Equity:</b> ₦${fmtMoneyOr0(
            sofpCur.totals.liabilities + sofpCur.totals.equity
          )}
            (Prior: ₦${fmtMoneyOr0(
              sofpPrev.totals.liabilities + sofpPrev.totals.equity
            )})</div>
        </div>
      `;

      return `
        ${titleHtml}
        <div class="subTitle">ASSETS</div>
        ${assetsTbl}
        <div class="subTitle">LIABILITIES</div>
        ${liabTbl}
        <div class="subTitle">EQUITY</div>
        ${eqTbl}
        ${totals}
        ${
          narrativeFilled ? `<div class="noteBox">${narrativeFilled}</div>` : ""
        }
      `;
    }

    // Cashflows page
    if (p.page_key === "cashflows") {
      const cur = computed?.cashflow?.cur || {};
      const prev = computed?.cashflow?.prev || {};

      const rows = [
        [
          "Net Earnings",
          `₦${fmtMoneyOr0(cur.netEarnings)}`,
          `₦${fmtMoneyOr0(prev.netEarnings)}`,
        ],
        [
          "Depreciation",
          `₦${fmtMoneyOr0(cur.depreciation)}`,
          `₦${fmtMoneyOr0(prev.depreciation)}`,
        ],
        [
          "Decrease in Accounts Receivable",
          `₦${fmtMoneyOr0(cur.arChange)}`,
          `₦${fmtMoneyOr0(prev.arChange)}`,
        ],
        [
          "Increase in Accounts Payable",
          `₦${fmtMoneyOr0(cur.apChange)}`,
          `₦${fmtMoneyOr0(prev.apChange)}`,
        ],
        [
          "Increase in Taxes Payable",
          `₦${fmtMoneyOr0(cur.taxPayableChange)}`,
          `₦${fmtMoneyOr0(prev.taxPayableChange)}`,
        ],
        [
          "Increase in Inventory",
          `₦${fmtMoneyOr0(-Math.abs(cur.inventoryChange || 0))}`,
          `₦${fmtMoneyOr0(-Math.abs(prev.inventoryChange || 0))}`,
        ],
        [
          "Net Cash from Operating Activities",
          `₦${fmtMoneyOr0(cur.netCashOps)}`,
          `₦${fmtMoneyOr0(prev.netCashOps)}`,
        ],
        [
          "Investing: Equipment Purchases",
          `₦${fmtMoneyOr0(-Math.abs(cur.equipmentCF || 0))}`,
          `₦${fmtMoneyOr0(-Math.abs(prev.equipmentCF || 0))}`,
        ],
        [
          "Financing: Notes Payable Proceeds",
          `₦${fmtMoneyOr0(cur.notesPayableCF)}`,
          `₦${fmtMoneyOr0(prev.notesPayableCF)}`,
        ],
        [
          "Net Cash Flow",
          `₦${fmtMoneyOr0(cur.totalCF)}`,
          `₦${fmtMoneyOr0(prev.totalCF)}`,
        ],
      ];

      const tbl = buildTableHtml({
        head: ["Cash Flow Item", "Current", "Prior"],
        rows,
        rightCols: [1, 2],
      });

      return `${titleHtml}${tbl}${
        narrativeFilled ? `<div class="noteBox">${narrativeFilled}</div>` : ""
      }`;
    }

    // Notes page (PL breakdown + SOFP breakdown + FA roll-forward)
    if (p.page_key === "notes") {
      const noteRev = buildTableHtml({
        head: ["Item", "Current", "Prior"],
        rows: rowsRev.map((x) => [
          htmlEscape(x.name),
          `₦${fmtMoneyOr0(x.cur)}`,
          `₦${fmtMoneyOr0(x.prev)}`,
        ]),
        rightCols: [1, 2],
      });

      const noteCos = buildTableHtml({
        head: ["Item", "Current", "Prior"],
        rows: rowsCos.map((x) => [
          htmlEscape(x.name),
          `₦${fmtMoneyOr0(x.cur)}`,
          `₦${fmtMoneyOr0(x.prev)}`,
        ]),
        rightCols: [1, 2],
      });

      const noteExp = buildTableHtml({
        head: ["Item", "Current", "Prior"],
        rows: rowsExp.map((x) => [
          htmlEscape(x.name),
          `₦${fmtMoneyOr0(x.cur)}`,
          `₦${fmtMoneyOr0(x.prev)}`,
        ]),
        rightCols: [1, 2],
      });

      // Balance sheet breakdown
      const bsAssets = buildTableHtml({
        head: ["Asset", "Current", "Prior"],
        rows: mergeSOFPByName(sofpCur.assets, sofpPrev.assets).map((x) => [
          htmlEscape(x.name),
          `₦${fmtMoneyOr0(x.cur)}`,
          `₦${fmtMoneyOr0(x.prev)}`,
        ]),
        rightCols: [1, 2],
      });

      const bsLiab = buildTableHtml({
        head: ["Liability", "Current", "Prior"],
        rows: mergeSOFPByName(sofpCur.liabilities, sofpPrev.liabilities).map(
          (x) => [
            htmlEscape(x.name),
            `₦${fmtMoneyOr0(x.cur)}`,
            `₦${fmtMoneyOr0(x.prev)}`,
          ]
        ),
        rightCols: [1, 2],
      });

      const bsEq = buildTableHtml({
        head: ["Equity", "Current", "Prior"],
        rows: mergeSOFPByName(sofpCur.equity, sofpPrev.equity).map((x) => [
          htmlEscape(x.name),
          `₦${fmtMoneyOr0(x.cur)}`,
          `₦${fmtMoneyOr0(x.prev)}`,
        ]),
        rightCols: [1, 2],
      });

      let faHtml = "";
      if (faCur && faPrev) {
        const faMove = buildTableHtml({
          head: ["Movement", "Current", "Prior"],
          rows: [
            [
              "Opening Cost",
              `₦${fmtMoneyOr0(faCur.openingCost)}`,
              `₦${fmtMoneyOr0(faPrev.openingCost)}`,
            ],
            [
              "Additions",
              `₦${fmtMoneyOr0(faCur.additionsCost)}`,
              `₦${fmtMoneyOr0(faPrev.additionsCost)}`,
            ],
            [
              "Disposals (Cost)",
              `₦${fmtMoneyOr0(faCur.disposalsCost)}`,
              `₦${fmtMoneyOr0(faPrev.disposalsCost)}`,
            ],
            [
              "Closing Cost",
              `₦${fmtMoneyOr0(faCur.closingCost)}`,
              `₦${fmtMoneyOr0(faPrev.closingCost)}`,
            ],
            [
              "Opening Accum Dep",
              `₦${fmtMoneyOr0(faCur.openingAccumDep)}`,
              `₦${fmtMoneyOr0(faPrev.openingAccumDep)}`,
            ],
            [
              "Depreciation for Period",
              `₦${fmtMoneyOr0(faCur.depThisPeriod)}`,
              `₦${fmtMoneyOr0(faPrev.depThisPeriod)}`,
            ],
            [
              "Accum Dep on Disposals",
              `₦${fmtMoneyOr0(faCur.accumDepOnDisposals)}`,
              `₦${fmtMoneyOr0(faPrev.accumDepOnDisposals)}`,
            ],
            [
              "Closing Accum Dep",
              `₦${fmtMoneyOr0(faCur.closingAccumDep)}`,
              `₦${fmtMoneyOr0(faPrev.closingAccumDep)}`,
            ],
            [
              "Closing NBV",
              `₦${fmtMoneyOr0(faCur.closingNBV)}`,
              `₦${fmtMoneyOr0(faPrev.closingNBV)}`,
            ],
          ],
          rightCols: [1, 2],
        });

        const mergeCats = () => {
          const m = new Map();
          for (const r of faPrev.byCategoryRows)
            m.set(r.category, { category: r.category, cur: null, prev: r });
          for (const r of faCur.byCategoryRows) {
            const e = m.get(r.category) || {
              category: r.category,
              cur: null,
              prev: null,
            };
            e.cur = r;
            m.set(r.category, e);
          }
          return Array.from(m.values()).sort(
            (a, b) => (b.cur?.nbv || 0) - (a.cur?.nbv || 0)
          );
        };

        const faCats = buildTableHtml({
          head: ["Category", "NBV (Current)", "NBV (Prior)"],
          rows: mergeCats().map((x) => [
            htmlEscape(x.category),
            `₦${fmtMoneyOr0(x.cur?.nbv || 0)}`,
            `₦${fmtMoneyOr0(x.prev?.nbv || 0)}`,
          ]),
          rightCols: [1, 2],
        });

        faHtml = `
          <div class="subTitle">NOTE 4: PROPERTY, PLANT AND EQUIPMENT (ROLL-FORWARD)</div>
          ${faMove}
          <div class="subTitle">NOTE 4A: PPE BY CATEGORY (CLOSING POSITION)</div>
          ${faCats}
        `;
      } else {
        faHtml = `
          <div class="muted" style="margin-top:10px;">
            Fixed assets roll-forward is not available (module missing, no data, or permission denied).
          </div>
        `;
      }

      return `
        ${titleHtml}
        <div class="subTitle">NOTE 1: REVENUE</div>
        ${noteRev}
        <div class="subTitle">NOTE 2: COST OF SALES</div>
        ${noteCos}
        <div class="subTitle">NOTE 3: OPERATING EXPENSES</div>
        ${noteExp}

        <div class="subTitle">BALANCE SHEET BREAKDOWN (ASSETS)</div>
        ${bsAssets}
        <div class="subTitle">BALANCE SHEET BREAKDOWN (LIABILITIES)</div>
        ${bsLiab}
        <div class="subTitle">BALANCE SHEET BREAKDOWN (EQUITY)</div>
        ${bsEq}

        ${faHtml}

        ${
          narrativeFilled ? `<div class="noteBox">${narrativeFilled}</div>` : ""
        }
      `;
    }

    // Value Added page
    if (p.page_key === "value_added") {
      const tbl = buildTableHtml({
        head: ["Description", "Current", "Prior"],
        rows: [
          [
            "Revenue",
            `₦${fmtMoneyOr0(plCur.revenue)}`,
            `₦${fmtMoneyOr0(plPrev.revenue)}`,
          ],
          [
            "Less: Bought-in materials & services (Cost of sales)",
            `₦${fmtMoneyOr0(plCur.cos)}`,
            `₦${fmtMoneyOr0(plPrev.cos)}`,
          ],
          [
            "Value Added",
            `₦${fmtMoneyOr0(valueAddedCur)}`,
            `₦${fmtMoneyOr0(valueAddedPrev)}`,
          ],
          ["Applied as follows:", "", ""],
          [
            "Salaries & wages (estimate)",
            `₦${fmtMoneyOr0(wagesCur)}`,
            `₦${fmtMoneyOr0(wagesPrev)}`,
          ],
          [
            "Depreciation (estimate)",
            `₦${fmtMoneyOr0(depCur)}`,
            `₦${fmtMoneyOr0(depPrev)}`,
          ],
          [
            "Interest/finance cost (estimate)",
            `₦${fmtMoneyOr0(interestCur)}`,
            `₦${fmtMoneyOr0(interestPrev)}`,
          ],
          [
            "Taxes (estimate)",
            `₦${fmtMoneyOr0(taxExpCur)}`,
            `₦${fmtMoneyOr0(taxExpPrev)}`,
          ],
          [
            "Retained profit",
            `₦${fmtMoneyOr0(plCur.net)}`,
            `₦${fmtMoneyOr0(plPrev.net)}`,
          ],
        ],
        rightCols: [1, 2],
      });

      return `${titleHtml}${tbl}${
        narrativeFilled ? `<div class="noteBox">${narrativeFilled}</div>` : ""
      }`;
    }

    // ✅ Tax page (UPDATED)
    if (p.page_key === "tax") {
      const tbl = buildTableHtml({
        head: ["Tax Computation", "Current", "Prior"],
        rows: [
          [
            "Profit/(loss) before tax (proxy)",
            `₦${fmtMoneyOr0(pbtCur)}`,
            `₦${fmtMoneyOr0(pbtPrev)}`,
          ],
          [
            "Add: Disallowable expenses",
            `₦${fmtMoneyOr0(disallowableCur)}`,
            `₦${fmtMoneyOr0(disallowablePrev)}`,
          ],
          [
            "Less: Capital allowances",
            `₦${fmtMoneyOr0(capitalAllowCur)}`,
            `₦${fmtMoneyOr0(capitalAllowPrev)}`,
          ],
          [
            "Taxable profit",
            `₦${fmtMoneyOr0(taxableProfitCur)}`,
            `₦${fmtMoneyOr0(taxableProfitPrev)}`,
          ],
          [
            `Companies Income Tax @ ${(citRateCur * 100).toFixed(0)}%`,
            `₦${fmtMoneyOr0(citCur)}`,
            `₦${fmtMoneyOr0(citPrev)}`,
          ],
          [
            `Development levy @ ${(devLevyRate() * 100).toFixed(0)}%`,
            `₦${fmtMoneyOr0(devCur)}`,
            `₦${fmtMoneyOr0(devPrev)}`,
          ],
          [
            "Total Tax (CIT + Development levy)",
            `₦${fmtMoneyOr0(totalTaxCur)}`,
            `₦${fmtMoneyOr0(totalTaxPrev)}`,
          ],
        ],
        rightCols: [1, 2],
      });

      const warn = `
        <div class="noteBox" style="border-color:#f59e0b;">
          <b>NOTE:</b> This is an auto-generated draft based on journal figures and fixed-assets data. Please review with a tax professional and edit accordingly.
        </div>
      `;

      return `${titleHtml}${tbl}${warn}${
        narrativeFilled ? `<div class="noteBox">${narrativeFilled}</div>` : ""
      }`;
    }

    // Default narrative pages: replaced placeholders + remove leftovers
    return `${titleHtml}${narrativeFilled}`;
  };

  // Render all included pages in order (excluding cover/toc which we build)
  const rest = included
    .filter((p) => !["cover", "toc"].includes(p.page_key))
    .map((p) => `<div class="page">${renderComputedPageHtml(p)}</div>`)
    .join("\n");

  return `<!doctype html><html><head>${css}</head><body>${cover}${toc}${rest}</body></html>`;
}

/* ------------------------ Word Export (seamless, no webpack polyfills) ------------------------ */

function downloadWordHtmlDoc({ fullHtml, filename }) {
  const bodyMatch = String(fullHtml || "").match(
    /<body[^>]*>([\s\S]*)<\/body>/i
  );
  const bodyOnly = bodyMatch ? bodyMatch[1] : String(fullHtml || "");

  // Word-friendly HTML container
  const wordHtml = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office"
          xmlns:w="urn:schemas-microsoft-com:office:word"
          xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="utf-8" />
        <title>${filename || "Financial Pack"}</title>
      </head>
      <body>${bodyOnly}</body>
    </html>
  `;

  const blob = new Blob([wordHtml], {
    type: "application/msword;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "KostPadi_Financial_Pack.doc";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Optional DOCX via edge function (does not break build)
async function downloadDocxViaEdge({ html, filename }) {
  try {
    const res = await supabase.functions.invoke("export-pack-docx", {
      body: { html, filename },
    });

    const base64 = res?.data?.base64;
    if (!base64) throw new Error("DOCX not available; missing base64 payload.");

    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++)
      byteNumbers[i] = byteChars.charCodeAt(i);
    const byteArray = new Uint8Array(byteNumbers);

    const blob = new Blob([byteArray], {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "KostPadi_Financial_Pack.docx";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return true;
  } catch {
    return false;
  }
}

/* ------------------------ Quill setup ------------------------ */

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link"],
    ["clean"],
  ],
};

/* ------------------------ main component ------------------------ */

export default function FinancialPackEditor() {
  const { packId } = useParams();

  const [pack, setPack] = useState(null);
  const [pages, setPages] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  const [html, setHtml] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const [computed, setComputed] = useState(null);

  const [companyInfo, setCompanyInfo] = useState(null);
  const [companyInfoErr, setCompanyInfoErr] = useState("");

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const previewIframeRef = useRef(null);

  // Fixed assets raw data (kept feature)
  const [faRaw, setFaRaw] = useState(null);

  const selected = useMemo(
    () => pages.find((p) => p.id === selectedId),
    [pages, selectedId]
  );

  // Safe computed defaults
  const plCurSafe = computed?.pl?.totalsCur || {
    revenue: 0,
    cos: 0,
    expense: 0,
    gross: 0,
    net: 0,
  };
  const plPrevSafe = computed?.pl?.totalsPrev || {
    revenue: 0,
    cos: 0,
    expense: 0,
    gross: 0,
    net: 0,
  };
  const cashCurSafe = computed?.cashflow?.cur || { totalCF: 0 };
  const cashPrevSafe = computed?.cashflow?.prev || { totalCF: 0 };

  useEffect(() => {
    if (!packId) return;
    loadAll();
    // eslint-disable-next-line
  }, [packId]);

  const loadAll = async () => {
    setLoading(true);

    const packRes = await supabase
      .from("fs_packs")
      .select("*")
      .eq("id", packId)
      .single();
    if (packRes.error) {
      setLoading(false);
      alert(packRes.error.message);
      return;
    }

    setPack(packRes.data);

    const pagesRes = await supabase
      .from("fs_pages")
      .select("*, fs_page_versions(id,version_no,content,created_at)")
      .eq("pack_id", packId)
      .order("sort_order", { ascending: true });

    if (pagesRes.error) {
      setLoading(false);
      alert(pagesRes.error.message);
      return;
    }

    const normalized = (pagesRes.data || []).map((p) => ({
      ...p,
      latest: latestVersion(p.fs_page_versions || []),
    }));

    setPages(normalized);

    if (normalized[0]?.id) {
      setSelectedId(normalized[0].id);
      setHtml(normalized[0]?.latest?.content?.html || "");
    }

    await loadComputed(packRes.data);
    await loadCompanyInfo(packRes.data.company_id);
    await loadFixedAssets(packRes.data.company_id);

    setLoading(false);
  };

  const loadCompanyInfo = async (companyId) => {
    if (!companyId) return;
    setCompanyInfoErr("");

    // ✅ Avoid 400 forever: select("*") and read safely.
    const res = await supabase
      .from("company_info")
      .select("*")
      .eq("company_id", companyId)
      .maybeSingle();

    if (res.error) {
      setCompanyInfoErr(res.error.message || "Failed to load company info");
      setCompanyInfo(null);
      return;
    }

    setCompanyInfo(res.data || null);
  };

  const loadFixedAssets = async (companyId) => {
    if (!companyId) return;

    // keep feature: try FA tables; if not present/permission denied, quietly skip
    try {
      const [aRes, dRes, lRes] = await Promise.all([
        supabase
          .from("fixed_assets")
          .select("id, asset_tag, name, category, purchase_date, cost")
          .eq("company_id", companyId),
        supabase
          .from("fixed_asset_disposals")
          .select("id, asset_id, disposal_date, proceeds, notes")
          .eq("company_id", companyId),
        supabase
          .from("fixed_asset_depreciation_lines")
          .select("id, asset_id, posting_date, depreciation_amount, run_id")
          .eq("company_id", companyId),
      ]);

      if (aRes.error || dRes.error || lRes.error) {
        setFaRaw(null);
        return;
      }

      setFaRaw({
        assets: aRes.data || [],
        disposals: dRes.data || [],
        depLines: lRes.data || [],
      });
    } catch {
      setFaRaw(null);
    }
  };

  const loadComputed = async (p) => {
    const startDate = p.start_date;
    const endDate = p.end_date;

    const priorStart = shiftYear(startDate, -1);
    const priorEnd = shiftYear(endDate, -1);

    const startMinus1 = minusOneDay(startDate);
    const priorStartMinus1 = minusOneDay(priorStart);

    const [
      plCur,
      plPrev,
      bsCurUpto,
      bsPrevUpto,
      bsBeginCur,
      bsBeginPrev,
      cfCur,
      cfPrev,
    ] = await Promise.all([
      supabase.rpc("get_pl_account_sums", {
        p_company_id: p.company_id,
        p_start_date: startDate,
        p_end_date: endDate,
      }),
      supabase.rpc("get_pl_account_sums", {
        p_company_id: p.company_id,
        p_start_date: priorStart,
        p_end_date: priorEnd,
      }),
      supabase.rpc("get_bs_account_sums_upto", {
        p_company_id: p.company_id,
        p_end_date: endDate,
      }),
      supabase.rpc("get_bs_account_sums_upto", {
        p_company_id: p.company_id,
        p_end_date: priorEnd,
      }),
      startMinus1
        ? supabase.rpc("get_bs_account_sums_upto", {
            p_company_id: p.company_id,
            p_end_date: startMinus1,
          })
        : Promise.resolve({ data: [], error: null }),
      priorStartMinus1
        ? supabase.rpc("get_bs_account_sums_upto", {
            p_company_id: p.company_id,
            p_end_date: priorStartMinus1,
          })
        : Promise.resolve({ data: [], error: null }),
      supabase.rpc("get_cf_section_sums", {
        p_company_id: p.company_id,
        p_start_date: startDate,
        p_end_date: endDate,
      }),
      supabase.rpc("get_cf_section_sums", {
        p_company_id: p.company_id,
        p_start_date: priorStart,
        p_end_date: priorEnd,
      }),
    ]);

    const errors = [
      plCur,
      plPrev,
      bsCurUpto,
      bsPrevUpto,
      bsBeginCur,
      bsBeginPrev,
      cfCur,
      cfPrev,
    ]
      .map((x) => x?.error)
      .filter(Boolean);

    if (errors.length) {
      alert(errors[0].message || "Failed to load computed data");
      setComputed(null);
      return;
    }

    const sum = (arr) => (arr || []).reduce((s, n) => s + (Number(n) || 0), 0);

    const calcPL = (rows) => {
      const revenue = sum(
        rows
          .filter((r) => String(r.account_type) === "Revenue")
          .map(
            (r) => (Number(r.total_credit) || 0) - (Number(r.total_debit) || 0)
          )
      );
      const cos = sum(
        rows
          .filter((r) => String(r.account_type) === "Cost of Sales")
          .map(
            (r) => (Number(r.total_debit) || 0) - (Number(r.total_credit) || 0)
          )
      );
      const expense = sum(
        rows
          .filter((r) => String(r.account_type) === "Expense")
          .map(
            (r) => (Number(r.total_debit) || 0) - (Number(r.total_credit) || 0)
          )
      );
      const gross = revenue - cos;
      const net = gross - expense;
      return { revenue, cos, expense, gross, net };
    };

    const plRowsCur = plCur.data || [];
    const plRowsPrev = plPrev.data || [];

    const plTotalsCur = calcPL(plRowsCur);
    const plTotalsPrev = calcPL(plRowsPrev);

    const calcCF = (plRows, beginRows, endRows, cfRow) => {
      const depreciation = sum(
        (plRows || [])
          .filter((r) => /depreciation/i.test(r.account_name || ""))
          .map((r) =>
            Math.abs(
              (Number(r.total_debit) || 0) - (Number(r.total_credit) || 0)
            )
          )
      );

      const debitNormalTypes = new Set(["asset", "expense"]);
      const balanceFromAggRows = (rows, needle, typeHint) => {
        const n = String(needle || "").toLowerCase();
        let bal = 0;
        for (const r of rows || []) {
          const name = String(r.account_name || "").toLowerCase();
          if (!name.includes(n)) continue;

          const dr = Number(r.total_debit) || 0;
          const cr = Number(r.total_credit) || 0;
          const type = String(r.account_type || typeHint || "").toLowerCase();
          if (debitNormalTypes.has(type)) bal += dr - cr;
          else bal += cr - dr;
        }
        return bal;
      };

      const netEarnings = calcPL(plRows).net;

      const arBegin = balanceFromAggRows(
        beginRows,
        "accounts receivable",
        "Asset"
      );
      const arEnd = balanceFromAggRows(endRows, "accounts receivable", "Asset");
      const arChange = -(arEnd - arBegin);

      const apBegin = balanceFromAggRows(
        beginRows,
        "accounts payable",
        "Liability"
      );
      const apEnd = balanceFromAggRows(
        endRows,
        "accounts payable",
        "Liability"
      );
      const apChange = apEnd - apBegin;

      const taxBegin = balanceFromAggRows(
        beginRows,
        "taxes payable",
        "Liability"
      );
      const taxEnd = balanceFromAggRows(endRows, "taxes payable", "Liability");
      const taxPayableChange = taxEnd - taxBegin;

      const invBegin = balanceFromAggRows(beginRows, "inventory", "Asset");
      const invEnd = balanceFromAggRows(endRows, "inventory", "Asset");
      const inventoryChange = invEnd - invBegin;

      const netCashOps =
        netEarnings +
        depreciation +
        arChange +
        apChange +
        taxPayableChange -
        inventoryChange;

      const investing = Number(cfRow?.investing_cf || 0);
      const financing = Number(cfRow?.financing_cf || 0);
      const totalCF = netCashOps + investing + financing;

      return {
        netEarnings,
        depreciation,
        arChange,
        apChange,
        taxPayableChange,
        inventoryChange,
        netCashOps,
        equipmentCF: investing,
        notesPayableCF: financing,
        totalCF,
      };
    };

    const cfCurRow = (cfCur.data && cfCur.data[0]) || {
      investing_cf: 0,
      financing_cf: 0,
    };
    const cfPrevRow = (cfPrev.data && cfPrev.data[0]) || {
      investing_cf: 0,
      financing_cf: 0,
    };

    const bsBeginCurRows = bsBeginCur.data || [];
    const bsBeginPrevRows = bsBeginPrev.data || [];

    const bsEndCurRows = bsCurUpto.data || [];
    const bsEndPrevRows = bsPrevUpto.data || [];

    const cfCurComputed = calcCF(
      plRowsCur,
      bsBeginCurRows,
      bsEndCurRows,
      cfCurRow
    );
    const cfPrevComputed = calcCF(
      plRowsPrev,
      bsBeginPrevRows,
      bsEndPrevRows,
      cfPrevRow
    );

    setComputed({
      period: { startDate, endDate, priorStart, priorEnd },
      pl: {
        rowsCur: plRowsCur,
        rowsPrev: plRowsPrev,
        totalsCur: plTotalsCur,
        totalsPrev: plTotalsPrev,
      },
      sofp: { endCur: bsEndCurRows, endPrev: bsEndPrevRows },
      cashflow: { cur: cfCurComputed, prev: cfPrevComputed },
    });
  };

  const onSelect = (p) => {
    setSelectedId(p.id);
    setHtml(p.latest?.content?.html || "");
  };

  const savePage = async () => {
    if (!selected) return;
    if (pack?.status === "locked") return alert("Pack is locked.");

    setSaving(true);

    const currentVer = selected.latest?.version_no || 0;
    const nextVer = currentVer + 1;

    const userRes = await supabase.auth.getUser();
    const user = userRes?.data?.user;

    if (!user?.id) {
      setSaving(false);
      alert("You must be signed in to save.");
      return;
    }

    const ins = await supabase.from("fs_page_versions").insert({
      page_id: selected.id,
      version_no: nextVer,
      content: { html },
      change_summary: "Edited",
      created_by: user.id,
    });

    setSaving(false);

    if (ins.error) return alert(ins.error.message);
    await loadAll();
  };

  const submitForReview = async () => {
    const note = prompt("Optional note:");
    const { error } = await supabase.rpc("rpc_submit_pack_for_review", {
      p_pack_id: packId,
      p_note: note || null,
    });
    if (error) return alert(error.message);
    await loadAll();
  };

  const approve = async () => {
    const note = prompt("Approval note (optional):");
    const { error } = await supabase.rpc("rpc_approve_pack", {
      p_pack_id: packId,
      p_note: note || null,
    });
    if (error) return alert(error.message);
    await loadAll();
  };

  const lock = async () => {
    const note = prompt("Lock note (optional):");
    const { error } = await supabase.rpc("rpc_lock_pack", {
      p_pack_id: packId,
      p_note: note || null,
    });
    if (error) return alert(error.message);
    await loadAll();
  };

  /* ------------------------ Auto-fill placeholders into Quill content ------------------------ */

  const autoFillPlaceholders = async () => {
    if (!selected) return;
    if (!computed) return alert("Computed data not loaded yet.");

    // include taxComputed so directors/tax placeholders also fill in editor
    const brand = parseBrandColors(companyInfo?.brand_colors);
    void brand;

    const builtTax = (() => {
      const startDate = computed?.period?.startDate;
      const endDate = computed?.period?.endDate;
      const priorStart = computed?.period?.priorStart;
      const priorEnd = computed?.period?.priorEnd;

      const plCur = computed?.pl?.totalsCur || {};
      const plPrev = computed?.pl?.totalsPrev || {};

      const capitalAllowCur =
        faRaw && startDate && endDate
          ? computeCapitalAllowanceFromFA(faRaw, startDate, endDate)
          : 0;
      const capitalAllowPrev =
        faRaw && priorStart && priorEnd
          ? computeCapitalAllowanceFromFA(faRaw, priorStart, priorEnd)
          : 0;

      const pbtCur = Number(plCur.net || 0);
      const pbtPrev = Number(plPrev.net || 0);

      const disallowableCur = 0;
      const disallowablePrev = 0;

      const taxableProfitCur = Math.max(
        0,
        pbtCur + disallowableCur - capitalAllowCur
      );
      const taxableProfitPrev = Math.max(
        0,
        pbtPrev + disallowablePrev - capitalAllowPrev
      );

      const citRateCur = citRateNew(plCur.revenue || 0);
      const citRatePrev = citRateNew(plPrev.revenue || 0);

      const citCur = taxableProfitCur * citRateCur;
      const citPrev = taxableProfitPrev * citRatePrev;

      const devCur = taxableProfitCur * devLevyRate();
      const devPrev = taxableProfitPrev * devLevyRate();

      return {
        cur: {
          pbt: pbtCur,
          disallowable: disallowableCur,
          capitalAllow: capitalAllowCur,
          taxableProfit: taxableProfitCur,
          citRate: citRateCur,
          cit: citCur,
          devRate: devLevyRate(),
          devLevy: devCur,
          totalTax: citCur + devCur,
        },
        prev: {
          pbt: pbtPrev,
          disallowable: disallowablePrev,
          capitalAllow: capitalAllowPrev,
          taxableProfit: taxableProfitPrev,
          citRate: citRatePrev,
          cit: citPrev,
          devRate: devLevyRate(),
          devLevy: devPrev,
          totalTax: citPrev + devPrev,
        },
      };
    })();

    const vars = getTemplateVars({
      pack,
      companyInfo,
      computed,
      taxComputed: builtTax,
    });

    // ✅ Fill + remove leftovers so editor never shows {{...}}
    const next = removeAnyRemainingPlaceholders(
      replacePlaceholders(html, vars),
      "0"
    );
    setHtml(next);
    alert("Placeholders filled into the editor content for this page.");
  };

  /* ------------------------ Preview Mode ------------------------ */

  const openPreview = async () => {
    if (!pack || !computed) return;

    const brand = parseBrandColors(companyInfo?.brand_colors);
    const built = buildPackHtml({
      pack,
      pages,
      companyInfo,
      computed,
      brand,
      faRaw,
    });

    setPreviewHtml(built);
    setPreviewOpen(true);

    setTimeout(() => {
      if (previewIframeRef.current) previewIframeRef.current.srcdoc = built;
    }, 0);
  };

  const closePreview = () => setPreviewOpen(false);

  /* ------------------------ Word Export (seamless) ------------------------ */

  const downloadWord = async () => {
    if (!pack || !computed) return;

    const brand = parseBrandColors(companyInfo?.brand_colors);
    const built = buildPackHtml({
      pack,
      pages,
      companyInfo,
      computed,
      brand,
      faRaw,
    });

    // Try DOCX via Edge Function (optional) -> fallback to .doc
    const ok = await downloadDocxViaEdge({
      html: built,
      filename: `KostPadi_Audit_Pack_${pack.end_date}.docx`,
    });

    if (!ok) {
      downloadWordHtmlDoc({
        fullHtml: built,
        filename: `KostPadi_Audit_Pack_${pack.end_date}.doc`,
      });
    }
  };

  /* ------------------------ PDF Export (ALL FEATURES KEPT + TAX UPDATED + CAP ALLOW ADDED) ------------------------ */

  const exportPackPDF = async () => {
    if (!pack || !computed) return;

    let info = companyInfo;
    if (!info) {
      const infoRes = await supabase
        .from("company_info")
        .select("*")
        .eq("company_id", pack.company_id)
        .maybeSingle();
      info = infoRes.data || null;
    }

    const companyName = info?.company_name || "Company";
    const tin = info?.TIN || "";
    const address = info?.address || "";
    const logoUrl = info?.logo_url || null;

    const brand = parseBrandColors(info?.brand_colors);
    const [priR, priG, priB] = hexToRgb(brand.primary);
    const [secR, secG, secB] = hexToRgb(brand.secondary);

    const logoDataUrl = await loadImageAsDataUrl(logoUrl);

    const { startDate, endDate, priorStart, priorEnd } = computed.period;
    const includedPages = pages.filter((p) => p.is_included);

    const doc = new jsPDF("p", "mm", "a4");
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const marginL = 15;
    const marginR = 15;

    // ---------- Header / Footer ----------
    const drawHeader = () => {
      doc.setFillColor(priR, priG, priB);
      doc.rect(0, 0, pageW, 12, "F");

      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text(companyName, marginL, 8);

      // ✅ PDF requirement: logo on ALL pages (keep)
      if (logoDataUrl) {
        try {
          const imgW = 18;
          const imgH = 18;
          const x = pageW - marginR - imgW;
          const y = 14;
          doc.addImage(logoDataUrl, "PNG", x, y, imgW, imgH, undefined, "FAST");
        } catch {}
      }

      doc.setTextColor(0, 0, 0);
    };

    const drawFooter = (label) => {
      const pageNum = doc.getCurrentPageInfo().pageNumber;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(90, 90, 90);
      doc.text(label || pack.title, marginL, pageH - 8);
      doc.text(`Page ${pageNum}`, pageW - marginR, pageH - 8, {
        align: "right",
      });
      doc.setTextColor(0, 0, 0);
    };

    const newPage = (title) => {
      doc.addPage();
      drawHeader();

      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.setTextColor(20, 20, 20);
      doc.text(title, marginL, 28);

      doc.setDrawColor(priR, priG, priB);
      doc.setLineWidth(0.6);
      doc.line(marginL, 31, pageW - marginR, 31);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(35, 35, 35);
    };

    // ---------- Cover ----------
    doc.setFillColor(priR, priG, priB);
    doc.rect(0, 0, pageW, 72, "F");
    doc.setFillColor(secR, secG, secB);
    doc.rect(0, 72, pageW, 12, "F");
    doc.setFillColor(245, 245, 245);
    doc.rect(0, 84, pageW, pageH - 84, "F");

    if (logoDataUrl) {
      try {
        const imgW = 42;
        const imgH = 42;
        doc.addImage(
          logoDataUrl,
          "PNG",
          pageW - marginR - imgW,
          18,
          imgW,
          imgH,
          undefined,
          "FAST"
        );
      } catch {}
    }

    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.text(companyName, marginL, 32);

    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text("AUDIT-FORMAT FINANCIAL STATEMENTS PACK", marginL, 44);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(`For the period: ${startDate} to ${endDate}`, marginL, 58);
    doc.setFont("helvetica", "normal");
    doc.text(`Comparative period: ${priorStart} to ${priorEnd}`, marginL, 66);

    doc.setTextColor(30, 30, 30);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Corporate Information", marginL, 102);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const infoLines = [
      `Registered Address: ${address || "—"}`,
      `TIN: ${tin || "—"}`,
      `Currency: ${pack.currency_code || "NGN"} (Rounded: ${
        pack.rounding_unit || "naira"
      })`,
      `Pack Status: ${pack.status}`,
    ];
    doc.text(
      doc.splitTextToSize(infoLines.join("\n"), pageW - marginL - marginR),
      marginL,
      110
    );

    drawFooter("Cover Page");

    // ---------- TOC ----------
    doc.addPage();
    drawHeader();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(20, 20, 20);
    doc.text("TABLE OF CONTENTS", marginL, 28);

    const tocRows = [];
    let pageCounter = 3;
    for (const p of includedPages) {
      if (p.page_key === "cover" || p.page_key === "toc") continue;
      tocRows.push([p.title, String(pageCounter)]);
      pageCounter += 1;
    }

    autoTable(doc, {
      startY: 36,
      head: [["Section", "Page"]],
      body: tocRows,
      theme: "grid",
      styles: { fontSize: 10 },
      headStyles: { fillColor: [priR, priG, priB], textColor: 255 },
      margin: { left: marginL, right: marginR },
    });

    drawFooter("Table of Contents");

    // Precompute notes helpers
    const headsCur = plToHeads(computed.pl.rowsCur || []);
    const headsPrev = plToHeads(computed.pl.rowsPrev || []);
    const rowsRev = mergePLHeads(headsCur, headsPrev, "Revenue");
    const rowsCos = mergePLHeads(headsCur, headsPrev, "Cost of Sales");
    const rowsExp = mergePLHeads(headsCur, headsPrev, "Expense");

    // SOFP buckets
    const sofpCur = sofpBuckets(computed.sofp.endCur || []);
    const sofpPrev = sofpBuckets(computed.sofp.endPrev || []);

    // Fixed assets roll-forward (kept)
    const faCur = faRaw
      ? computeFARollForward(faRaw, startDate, endDate)
      : null;
    const faPrev = faRaw
      ? computeFARollForward(faRaw, priorStart, priorEnd)
      : null;

    // Value added (kept)
    const wagesCur = pickByRegex(
      computed.pl.rowsCur,
      /(salary|wage|staff|payroll)/i
    );
    const wagesPrev = pickByRegex(
      computed.pl.rowsPrev,
      /(salary|wage|staff|payroll)/i
    );
    const depCur = pickByRegex(computed.pl.rowsCur, /(depreciation)/i);
    const depPrev = pickByRegex(computed.pl.rowsPrev, /(depreciation)/i);
    const interestCur = pickByRegex(
      computed.pl.rowsCur,
      /(interest|finance cost|loan)/i
    );
    const interestPrev = pickByRegex(
      computed.pl.rowsPrev,
      /(interest|finance cost|loan)/i
    );
    const taxExpCur = pickByRegex(computed.pl.rowsCur, /(tax)/i);
    const taxExpPrev = pickByRegex(computed.pl.rowsPrev, /(tax)/i);

    const valueAddedCur =
      computed.pl.totalsCur.revenue - computed.pl.totalsCur.cos;
    const valueAddedPrev =
      computed.pl.totalsPrev.revenue - computed.pl.totalsPrev.cos;

    // ✅ Capital allowance + tax (UPDATED)
    const capitalAllowCur = faRaw
      ? computeCapitalAllowanceFromFA(faRaw, startDate, endDate)
      : 0;
    const capitalAllowPrev = faRaw
      ? computeCapitalAllowanceFromFA(faRaw, priorStart, priorEnd)
      : 0;

    const turnoverCur = computed.pl.totalsCur.revenue;
    const turnoverPrev = computed.pl.totalsPrev.revenue;

    const citRateCur = citRateNew(turnoverCur);
    const citRatePrev = citRateNew(turnoverPrev);

    const pbtCur = computed.pl.totalsCur.net; // proxy (kept)
    const pbtPrev = computed.pl.totalsPrev.net;

    const disallowableCur = 0;
    const disallowablePrev = 0;

    const taxableProfitCur = Math.max(
      0,
      pbtCur + disallowableCur - capitalAllowCur
    );
    const taxableProfitPrev = Math.max(
      0,
      pbtPrev + disallowablePrev - capitalAllowPrev
    );

    const citCur = taxableProfitCur * citRateCur;
    const citPrev = taxableProfitPrev * citRatePrev;

    const devCur = taxableProfitCur * devLevyRate();
    const devPrev = taxableProfitPrev * devLevyRate();

    const totalTaxCur = citCur + devCur;
    const totalTaxPrev = citPrev + devPrev;

    // ---------- Export included sections ----------
    for (const p of includedPages) {
      if (p.page_key === "cover" || p.page_key === "toc") continue;

      newPage(p.title);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      doc.text(
        `Current: ${startDate} → ${endDate}    Comparative: ${priorStart} → ${priorEnd}`,
        marginL,
        38
      );
      doc.setTextColor(35, 35, 35);
      doc.setFontSize(10);

      // Corporate Info
      if (p.page_key === "corporate_info") {
        autoTable(doc, {
          startY: 44,
          head: [["Field", "Value"]],
          body: [
            ["Company Name", companyName],
            ["Registered Address", address || "—"],
            ["TIN", tin || "—"],
            ["RC Number", info?.rc_number || "—"],
            ["Incorporation Date", info?.incorporation_date || "—"],
            ["Reporting Period", `${startDate} → ${endDate}`],
            ["Comparative Period", `${priorStart} → ${priorEnd}`],
          ],
          theme: "grid",
          styles: { fontSize: 10 },
          headStyles: { fillColor: [priR, priG, priB], textColor: 255 },
          margin: { left: marginL, right: marginR },
        });

        const extra = stripHtml(p.latest?.content?.html || "");
        if (extra) {
          const y = (doc.lastAutoTable?.finalY || 44) + 10;
          doc.text(
            doc.splitTextToSize(extra, pageW - marginL - marginR),
            marginL,
            y
          );
        }

        drawFooter(p.title);
        continue;
      }

      // PL
      if (p.page_key === "pl") {
        const cur = computed.pl.totalsCur;
        const prev = computed.pl.totalsPrev;

        const body = [
          [
            "Revenue",
            `₦${fmtMoneyOr0(cur.revenue)}`,
            `₦${fmtMoneyOr0(prev.revenue)}`,
          ],
          ...rowsRev.map((x) => [
            x.name,
            `₦${fmtMoneyOr0(x.cur)}`,
            `₦${fmtMoneyOr0(x.prev)}`,
          ]),
          [
            "Cost of Sales",
            `₦${fmtMoneyOr0(cur.cos)}`,
            `₦${fmtMoneyOr0(prev.cos)}`,
          ],
          ...rowsCos.map((x) => [
            x.name,
            `₦${fmtMoneyOr0(x.cur)}`,
            `₦${fmtMoneyOr0(x.prev)}`,
          ]),
          [
            "Gross Profit",
            `₦${fmtMoneyOr0(cur.gross)}`,
            `₦${fmtMoneyOr0(prev.gross)}`,
          ],
          [
            "Operating Expenses",
            `₦${fmtMoneyOr0(cur.expense)}`,
            `₦${fmtMoneyOr0(prev.expense)}`,
          ],
          ...rowsExp.map((x) => [
            x.name,
            `₦${fmtMoneyOr0(x.cur)}`,
            `₦${fmtMoneyOr0(x.prev)}`,
          ]),
          [
            cur.net >= 0 ? "Net Profit" : "Net Loss",
            `₦${fmtMoneyOr0(cur.net)}`,
            `₦${fmtMoneyOr0(prev.net)}`,
          ],
        ];

        autoTable(doc, {
          startY: 44,
          head: [["Description", "Current", "Prior"]],
          body,
          theme: "grid",
          styles: { fontSize: 10 },
          headStyles: { fillColor: [priR, priG, priB], textColor: 255 },
          columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
          margin: { left: marginL, right: marginR },
        });

        const notes = stripHtml(p.latest?.content?.html || "");
        if (notes) {
          const y = (doc.lastAutoTable?.finalY || 44) + 10;
          doc.text(
            doc.splitTextToSize(notes, pageW - marginL - marginR),
            marginL,
            y
          );
        }

        drawFooter(p.title);
        continue;
      }

      // SOFP
      if (p.page_key === "sofp") {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text("ASSETS", marginL, 44);

        autoTable(doc, {
          startY: 48,
          head: [["Assets", "Current", "Prior"]],
          body: mergeSOFPByName(sofpCur.assets, sofpPrev.assets).map((x) => [
            x.name,
            `₦${fmtMoneyOr0(x.cur)}`,
            `₦${fmtMoneyOr0(x.prev)}`,
          ]),
          theme: "grid",
          styles: { fontSize: 10 },
          headStyles: { fillColor: [priR, priG, priB], textColor: 255 },
          columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
          margin: { left: marginL, right: marginR },
        });

        let y = (doc.lastAutoTable?.finalY || 48) + 10;

        doc.text("LIABILITIES", marginL, y);
        autoTable(doc, {
          startY: y + 4,
          head: [["Liabilities", "Current", "Prior"]],
          body: mergeSOFPByName(sofpCur.liabilities, sofpPrev.liabilities).map(
            (x) => [x.name, `₦${fmtMoneyOr0(x.cur)}`, `₦${fmtMoneyOr0(x.prev)}`]
          ),
          theme: "grid",
          styles: { fontSize: 10 },
          headStyles: { fillColor: [priR, priG, priB], textColor: 255 },
          columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
          margin: { left: marginL, right: marginR },
        });

        y = (doc.lastAutoTable?.finalY || y) + 10;

        doc.text("EQUITY", marginL, y);
        autoTable(doc, {
          startY: y + 4,
          head: [["Equity", "Current", "Prior"]],
          body: mergeSOFPByName(sofpCur.equity, sofpPrev.equity).map((x) => [
            x.name,
            `₦${fmtMoneyOr0(x.cur)}`,
            `₦${fmtMoneyOr0(x.prev)}`,
          ]),
          theme: "grid",
          styles: { fontSize: 10 },
          headStyles: { fillColor: [priR, priG, priB], textColor: 255 },
          columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
          margin: { left: marginL, right: marginR },
        });

        const totalsY = (doc.lastAutoTable?.finalY || y) + 10;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text(
          `Total Assets: ₦${fmtMoneyOr0(
            sofpCur.totals.assets
          )} (Prior: ₦${fmtMoneyOr0(sofpPrev.totals.assets)})`,
          marginL,
          totalsY
        );
        doc.text(
          `Total Liabilities + Equity: ₦${fmtMoneyOr0(
            sofpCur.totals.liabilities + sofpCur.totals.equity
          )} (Prior: ₦${fmtMoneyOr0(
            sofpPrev.totals.liabilities + sofpPrev.totals.equity
          )})`,
          marginL,
          totalsY + 6
        );

        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);

        const extra = stripHtml(p.latest?.content?.html || "");
        if (extra) {
          doc.text(
            doc.splitTextToSize(extra, pageW - marginL - marginR),
            marginL,
            totalsY + 16
          );
        }

        drawFooter(p.title);
        continue;
      }

      // Cashflows
      if (p.page_key === "cashflows") {
        const cur = computed.cashflow.cur;
        const prev = computed.cashflow.prev;

        autoTable(doc, {
          startY: 44,
          head: [["Cash Flow Item", "Current", "Prior"]],
          body: [
            [
              "Net Earnings",
              `₦${fmtMoneyOr0(cur.netEarnings)}`,
              `₦${fmtMoneyOr0(prev.netEarnings)}`,
            ],
            [
              "Depreciation",
              `₦${fmtMoneyOr0(cur.depreciation)}`,
              `₦${fmtMoneyOr0(prev.depreciation)}`,
            ],
            [
              "Decrease in Accounts Receivable",
              `₦${fmtMoneyOr0(cur.arChange)}`,
              `₦${fmtMoneyOr0(prev.arChange)}`,
            ],
            [
              "Increase in Accounts Payable",
              `₦${fmtMoneyOr0(cur.apChange)}`,
              `₦${fmtMoneyOr0(prev.apChange)}`,
            ],
            [
              "Increase in Taxes Payable",
              `₦${fmtMoneyOr0(cur.taxPayableChange)}`,
              `₦${fmtMoneyOr0(prev.taxPayableChange)}`,
            ],
            [
              "Increase in Inventory",
              `₦${fmtMoneyOr0(-Math.abs(cur.inventoryChange))}`,
              `₦${fmtMoneyOr0(-Math.abs(prev.inventoryChange))}`,
            ],
            [
              "Net Cash from Operating Activities",
              `₦${fmtMoneyOr0(cur.netCashOps)}`,
              `₦${fmtMoneyOr0(prev.netCashOps)}`,
            ],
            [
              "Investing: Equipment Purchases",
              `₦${fmtMoneyOr0(-Math.abs(cur.equipmentCF))}`,
              `₦${fmtMoneyOr0(-Math.abs(prev.equipmentCF))}`,
            ],
            [
              "Financing: Notes Payable Proceeds",
              `₦${fmtMoneyOr0(cur.notesPayableCF)}`,
              `₦${fmtMoneyOr0(prev.notesPayableCF)}`,
            ],
            [
              "Net Cash Flow",
              `₦${fmtMoneyOr0(cur.totalCF)}`,
              `₦${fmtMoneyOr0(prev.totalCF)}`,
            ],
          ],
          theme: "grid",
          styles: { fontSize: 10 },
          headStyles: { fillColor: [priR, priG, priB], textColor: 255 },
          columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
          margin: { left: marginL, right: marginR },
        });

        const extra = stripHtml(p.latest?.content?.html || "");
        if (extra) {
          const y = (doc.lastAutoTable?.finalY || 44) + 10;
          doc.text(
            doc.splitTextToSize(extra, pageW - marginL - marginR),
            marginL,
            y
          );
        }

        drawFooter(p.title);
        continue;
      }

      // Notes (expanded + fixed assets retained)
      if (p.page_key === "notes") {
        const printNoteTable = (title, rows, startY) => {
          doc.setFont("helvetica", "bold");
          doc.setFontSize(11);
          doc.text(title, marginL, startY);
          doc.setFont("helvetica", "normal");
          doc.setFontSize(10);

          autoTable(doc, {
            startY: startY + 4,
            head: [["Item", "Current", "Prior"]],
            body: rows.map((x) => [
              x.name,
              `₦${fmtMoneyOr0(x.cur)}`,
              `₦${fmtMoneyOr0(x.prev)}`,
            ]),
            theme: "grid",
            styles: { fontSize: 10 },
            headStyles: { fillColor: [priR, priG, priB], textColor: 255 },
            columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
            margin: { left: marginL, right: marginR },
          });

          return (doc.lastAutoTable?.finalY || startY + 20) + 10;
        };

        let y = 44;
        y = printNoteTable("NOTE 1: REVENUE", rowsRev, y);
        if (y > 250) {
          newPage("Notes to the Financial Statements (cont.)");
          y = 44;
        }

        y = printNoteTable("NOTE 2: COST OF SALES", rowsCos, y);
        if (y > 250) {
          newPage("Notes to the Financial Statements (cont.)");
          y = 44;
        }

        y = printNoteTable("NOTE 3: OPERATING EXPENSES", rowsExp, y);

        const printSOFPNote = (title, curArr, prevArr) => {
          if (y > 240) {
            newPage("Notes to the Financial Statements (cont.)");
            y = 44;
          }
          doc.setFont("helvetica", "bold");
          doc.setFontSize(11);
          doc.text(title, marginL, y);
          doc.setFont("helvetica", "normal");
          doc.setFontSize(10);

          autoTable(doc, {
            startY: y + 4,
            head: [["Item", "Current", "Prior"]],
            body: mergeSOFPByName(curArr, prevArr).map((x) => [
              x.name,
              `₦${fmtMoneyOr0(x.cur)}`,
              `₦${fmtMoneyOr0(x.prev)}`,
            ]),
            theme: "grid",
            styles: { fontSize: 10 },
            headStyles: { fillColor: [priR, priG, priB], textColor: 255 },
            columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
            margin: { left: marginL, right: marginR },
          });

          y = (doc.lastAutoTable?.finalY || y) + 10;
        };

        printSOFPNote(
          "NOTE 3A: ASSETS BREAKDOWN",
          sofpCur.assets,
          sofpPrev.assets
        );
        printSOFPNote(
          "NOTE 3B: LIABILITIES BREAKDOWN",
          sofpCur.liabilities,
          sofpPrev.liabilities
        );
        printSOFPNote(
          "NOTE 3C: EQUITY BREAKDOWN",
          sofpCur.equity,
          sofpPrev.equity
        );

        if (faRaw && faCur && faPrev) {
          if (y > 220) {
            newPage("Notes to the Financial Statements (cont.)");
            y = 44;
          }

          doc.setFont("helvetica", "bold");
          doc.setFontSize(11);
          doc.text(
            "NOTE 4: PROPERTY, PLANT AND EQUIPMENT (ROLL-FORWARD)",
            marginL,
            y
          );
          doc.setFont("helvetica", "normal");
          doc.setFontSize(10);

          autoTable(doc, {
            startY: y + 4,
            head: [["Movement", "Current", "Prior"]],
            body: [
              [
                "Opening Cost",
                `₦${fmtMoneyOr0(faCur.openingCost)}`,
                `₦${fmtMoneyOr0(faPrev.openingCost)}`,
              ],
              [
                "Additions",
                `₦${fmtMoneyOr0(faCur.additionsCost)}`,
                `₦${fmtMoneyOr0(faPrev.additionsCost)}`,
              ],
              [
                "Disposals (Cost)",
                `₦${fmtMoneyOr0(faCur.disposalsCost)}`,
                `₦${fmtMoneyOr0(faPrev.disposalsCost)}`,
              ],
              [
                "Closing Cost",
                `₦${fmtMoneyOr0(faCur.closingCost)}`,
                `₦${fmtMoneyOr0(faPrev.closingCost)}`,
              ],
              [
                "Opening Accum Dep",
                `₦${fmtMoneyOr0(faCur.openingAccumDep)}`,
                `₦${fmtMoneyOr0(faPrev.openingAccumDep)}`,
              ],
              [
                "Depreciation for Period",
                `₦${fmtMoneyOr0(faCur.depThisPeriod)}`,
                `₦${fmtMoneyOr0(faPrev.depThisPeriod)}`,
              ],
              [
                "Accum Dep on Disposals",
                `₦${fmtMoneyOr0(faCur.accumDepOnDisposals)}`,
                `₦${fmtMoneyOr0(faPrev.accumDepOnDisposals)}`,
              ],
              [
                "Closing Accum Dep",
                `₦${fmtMoneyOr0(faCur.closingAccumDep)}`,
                `₦${fmtMoneyOr0(faPrev.closingAccumDep)}`,
              ],
              [
                "Closing NBV",
                `₦${fmtMoneyOr0(faCur.closingNBV)}`,
                `₦${fmtMoneyOr0(faPrev.closingNBV)}`,
              ],
            ],
            theme: "grid",
            styles: { fontSize: 10 },
            headStyles: { fillColor: [priR, priG, priB], textColor: 255 },
            columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
            margin: { left: marginL, right: marginR },
          });

          y = (doc.lastAutoTable?.finalY || y) + 10;

          if (y > 250) {
            newPage("Notes to the Financial Statements (cont.)");
            y = 44;
          }

          doc.setFont("helvetica", "bold");
          doc.setFontSize(11);
          doc.text("NOTE 4A: PPE BY CATEGORY (CLOSING POSITION)", marginL, y);
          doc.setFont("helvetica", "normal");
          doc.setFontSize(10);

          const mergeCats = () => {
            const m = new Map();
            for (const r of faPrev.byCategoryRows)
              m.set(r.category, { category: r.category, cur: null, prev: r });
            for (const r of faCur.byCategoryRows) {
              const e = m.get(r.category) || {
                category: r.category,
                cur: null,
                prev: null,
              };
              e.cur = r;
              m.set(r.category, e);
            }
            return Array.from(m.values()).sort(
              (a, b) => (b.cur?.nbv || 0) - (a.cur?.nbv || 0)
            );
          };

          autoTable(doc, {
            startY: y + 4,
            head: [["Category", "NBV (Current)", "NBV (Prior)"]],
            body: mergeCats().map((x) => [
              x.category,
              `₦${fmtMoneyOr0(x.cur?.nbv || 0)}`,
              `₦${fmtMoneyOr0(x.prev?.nbv || 0)}`,
            ]),
            theme: "grid",
            styles: { fontSize: 10 },
            headStyles: { fillColor: [priR, priG, priB], textColor: 255 },
            columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
            margin: { left: marginL, right: marginR },
          });

          y = (doc.lastAutoTable?.finalY || y) + 10;
        }

        const extra = stripHtml(p.latest?.content?.html || "");
        if (extra) {
          if (y > 250) {
            newPage("Notes to the Financial Statements (cont.)");
            y = 44;
          }
          doc.text(
            doc.splitTextToSize(extra, pageW - marginL - marginR),
            marginL,
            y
          );
        }

        drawFooter(p.title);
        continue;
      }

      // Value Added
      if (p.page_key === "value_added") {
        autoTable(doc, {
          startY: 44,
          head: [["Description", "Current", "Prior"]],
          body: [
            [
              "Revenue",
              `₦${fmtMoneyOr0(computed.pl.totalsCur.revenue)}`,
              `₦${fmtMoneyOr0(computed.pl.totalsPrev.revenue)}`,
            ],
            [
              "Less: Bought-in materials & services (Cost of sales)",
              `₦${fmtMoneyOr0(computed.pl.totalsCur.cos)}`,
              `₦${fmtMoneyOr0(computed.pl.totalsPrev.cos)}`,
            ],
            [
              "Value Added",
              `₦${fmtMoneyOr0(valueAddedCur)}`,
              `₦${fmtMoneyOr0(valueAddedPrev)}`,
            ],
            ["Applied as follows:", "", ""],
            [
              "Salaries & wages (estimate)",
              `₦${fmtMoneyOr0(wagesCur)}`,
              `₦${fmtMoneyOr0(wagesPrev)}`,
            ],
            [
              "Depreciation (estimate)",
              `₦${fmtMoneyOr0(depCur)}`,
              `₦${fmtMoneyOr0(depPrev)}`,
            ],
            [
              "Interest/finance cost (estimate)",
              `₦${fmtMoneyOr0(interestCur)}`,
              `₦${fmtMoneyOr0(interestPrev)}`,
            ],
            [
              "Taxes (estimate)",
              `₦${fmtMoneyOr0(taxExpCur)}`,
              `₦${fmtMoneyOr0(taxExpPrev)}`,
            ],
            [
              "Retained profit",
              `₦${fmtMoneyOr0(computed.pl.totalsCur.net)}`,
              `₦${fmtMoneyOr0(computed.pl.totalsPrev.net)}`,
            ],
          ],
          theme: "grid",
          styles: { fontSize: 10 },
          headStyles: { fillColor: [priR, priG, priB], textColor: 255 },
          columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
          margin: { left: marginL, right: marginR },
        });

        const extra = stripHtml(p.latest?.content?.html || "");
        if (extra) {
          const y = (doc.lastAutoTable?.finalY || 44) + 10;
          doc.text(
            doc.splitTextToSize(extra, pageW - marginL - marginR),
            marginL,
            y
          );
        }

        drawFooter(p.title);
        continue;
      }

      // ✅ Tax (UPDATED: CIT + Dev Levy only, includes capital allowance)
      if (p.page_key === "tax") {
        autoTable(doc, {
          startY: 44,
          head: [["Tax Computation", "Current", "Prior"]],
          body: [
            [
              "Profit/(loss) before tax (proxy)",
              `₦${fmtMoneyOr0(pbtCur)}`,
              `₦${fmtMoneyOr0(pbtPrev)}`,
            ],
            [
              "Add: Disallowable expenses",
              `₦${fmtMoneyOr0(disallowableCur)}`,
              `₦${fmtMoneyOr0(disallowablePrev)}`,
            ],
            [
              "Less: Capital allowances",
              `₦${fmtMoneyOr0(capitalAllowCur)}`,
              `₦${fmtMoneyOr0(capitalAllowPrev)}`,
            ],
            [
              "Taxable profit",
              `₦${fmtMoneyOr0(taxableProfitCur)}`,
              `₦${fmtMoneyOr0(taxableProfitPrev)}`,
            ],
            [
              `Companies Income Tax @ ${(citRateCur * 100).toFixed(0)}%`,
              `₦${fmtMoneyOr0(citCur)}`,
              `₦${fmtMoneyOr0(citPrev)}`,
            ],
            [
              `Development levy @ ${(devLevyRate() * 100).toFixed(0)}%`,
              `₦${fmtMoneyOr0(devCur)}`,
              `₦${fmtMoneyOr0(devPrev)}`,
            ],
            [
              "Total Tax (CIT + Development levy)",
              `₦${fmtMoneyOr0(totalTaxCur)}`,
              `₦${fmtMoneyOr0(totalTaxPrev)}`,
            ],
          ],
          theme: "grid",
          styles: { fontSize: 10 },
          headStyles: { fillColor: [priR, priG, priB], textColor: 255 },
          columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
          margin: { left: marginL, right: marginR },
        });

        const warnY = (doc.lastAutoTable?.finalY || 44) + 8;
        doc.setFont("helvetica", "italic");
        doc.setFontSize(9);
        doc.setTextColor(120, 60, 60);
        doc.text(
          "NOTE: This is an auto-generated draft based on journal figures and fixed-assets data. Please review with a tax professional and edit accordingly.",
          marginL,
          warnY
        );
        doc.setTextColor(35, 35, 35);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);

        const extra = stripHtml(p.latest?.content?.html || "");
        if (extra) {
          doc.text(
            doc.splitTextToSize(extra, pageW - marginL - marginR),
            marginL,
            warnY + 10
          );
        }

        drawFooter(p.title);
        continue;
      }

      // Default narrative pages (PDF)
      // PDF uses stripHtml; placeholders in narrative should already be removed if user used auto-fill,
      // but to guarantee NO placeholders in PDF, we hard-clean the text here too.
      const rawHtml = p.latest?.content?.html || "";
      const brandX = parseBrandColors(info?.brand_colors);
      void brandX;

      const taxComputedForVars = {
        cur: {
          pbt: pbtCur,
          disallowable: disallowableCur,
          capitalAllow: capitalAllowCur,
          taxableProfit: taxableProfitCur,
          citRate: citRateCur,
          cit: citCur,
          devRate: devLevyRate(),
          devLevy: devCur,
          totalTax: totalTaxCur,
        },
        prev: {
          pbt: pbtPrev,
          disallowable: disallowablePrev,
          capitalAllow: capitalAllowPrev,
          taxableProfit: taxableProfitPrev,
          citRate: citRatePrev,
          cit: citPrev,
          devRate: devLevyRate(),
          devLevy: devPrev,
          totalTax: totalTaxPrev,
        },
      };

      const vars = getTemplateVars({
        pack,
        companyInfo: info,
        computed,
        taxComputed: taxComputedForVars,
      });

      const cleaned = removeAnyRemainingPlaceholders(
        replacePlaceholders(rawHtml, vars),
        "0"
      );

      const text = stripHtml(cleaned || "");
      doc.text(
        doc.splitTextToSize(text || "0", pageW - marginL - marginR),
        marginL,
        44
      );
      drawFooter(p.title);
    }

    doc.save(`KostPadi_Audit_Pack_${computed.period.endDate}.pdf`);
  };

  /* ------------------------ render ------------------------ */

  if (loading) return <div className="pack-wrap">Loading…</div>;
  if (!pack) return <div className="pack-wrap">Pack not found.</div>;

  return (
    <div className="pack-wrap">
      <div
        className="pack-header"
        style={{ position: "sticky", top: 0, zIndex: 50 }}
      >
        <div>
          <h2>{pack.title}</h2>
          <p>
            Status: <b>{pack.status}</b> • Period: {pack.start_date} →{" "}
            {pack.end_date}
          </p>
          {companyInfoErr && (
            <p className="muted" style={{ color: "#ffb4b4" }}>
              Company info warning: {companyInfoErr}
            </p>
          )}
          {!computed && (
            <p className="muted" style={{ color: "#ffd6a5" }}>
              Computed data not ready — export/preview buttons will enable once
              RPCs load successfully.
            </p>
          )}
        </div>

        <div className="actions" style={{ pointerEvents: "auto" }}>
          <button className="btn" onClick={openPreview} disabled={!computed}>
            Preview Pack
          </button>

          <button className="btn" onClick={downloadWord} disabled={!computed}>
            Download Word
          </button>

          <button className="btn" onClick={exportPackPDF} disabled={!computed}>
            Export Pack PDF
          </button>

          {pack.status === "draft" && (
            <button className="btn" onClick={submitForReview}>
              Submit for Review
            </button>
          )}

          {pack.status === "under_review" && (
            <button className="btn" onClick={approve}>
              Approve
            </button>
          )}

          {pack.status === "approved" && (
            <button className="btn primary" onClick={lock}>
              Lock Final
            </button>
          )}
        </div>
      </div>

      <div className="editor-grid" style={{ position: "relative", zIndex: 1 }}>
        <aside className="sidebar">
          <h3>Pages</h3>
          {pages.map((p) => (
            <div
              key={p.id}
              className={`page-item ${selectedId === p.id ? "active" : ""}`}
              onClick={() => onSelect(p)}
            >
              <div className="page-title">{p.title}</div>
              <div className="page-meta">
                {p.page_key} • v{p.latest?.version_no || 0}
              </div>
            </div>
          ))}
        </aside>

        <main className="main">
          <div className="card">
            <div className="row-head">
              <h3>{selected?.title}</h3>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  className="btn"
                  onClick={autoFillPlaceholders}
                  disabled={!computed || !selected}
                  title="Replaces {{placeholders}} in this page with live values (and removes any leftover placeholders)."
                >
                  Auto-fill Placeholders
                </button>

                <button
                  className="btn primary"
                  onClick={savePage}
                  disabled={saving || pack.status === "locked"}
                >
                  {saving ? "Saving..." : "Save Page"}
                </button>
              </div>
            </div>

            <p className="muted">
              Statement pages (Profit/Loss, SOFP, Cashflows, Value Added, Tax,
              Notes) are computed from journal-based RPCs. Use this editor for
              disclosures and narrative. You can insert placeholders like{" "}
              <b>{"{{company_name}}"}</b>, <b>{"{{pl_revenue}}"}</b>,{" "}
              <b>{"{{pl_gross}}"}</b>, <b>{"{{pbt}}"}</b>, <b>{"{{pl_net}}"}</b>
              , then click <b>Auto-fill Placeholders</b>.
              <br />✅ This version guarantees that no raw placeholders like{" "}
              <b>{"{{pl_profit}}"}</b> will appear in Preview/Word/PDF.
            </p>

            <ReactQuill
              theme="snow"
              value={html}
              onChange={setHtml}
              modules={quillModules}
            />
          </div>

          <div className="card">
            <h3>Computed snapshot (journal-based)</h3>
            {!computed ? (
              <p className="muted">Loading computed data…</p>
            ) : (
              <div className="kpis">
                <div className="kpi">
                  <div className="kpi-label">Revenue (Current)</div>
                  <div className="kpi-value">
                    ₦{fmtMoneyOr0(plCurSafe.revenue)}
                  </div>
                  <div className="kpi-sub">
                    Prior: ₦{fmtMoneyOr0(plPrevSafe.revenue)}
                  </div>
                </div>

                <div className="kpi">
                  <div className="kpi-label">Net Profit/(Loss) (Current)</div>
                  <div className="kpi-value">₦{fmtMoneyOr0(plCurSafe.net)}</div>
                  <div className="kpi-sub">
                    Prior: ₦{fmtMoneyOr0(plPrevSafe.net)}
                  </div>
                </div>

                <div className="kpi">
                  <div className="kpi-label">Net Cash Flow (Current)</div>
                  <div className="kpi-value">
                    ₦{fmtMoneyOr0(cashCurSafe.totalCF)}
                  </div>
                  <div className="kpi-sub">
                    Prior: ₦{fmtMoneyOr0(cashPrevSafe.totalCF)}
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Preview Modal */}
      {previewOpen && (
        <div className="pack-preview-overlay" onClick={closePreview}>
          <div
            className="pack-preview-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pack-preview-head">
              <div>
                <div style={{ fontWeight: 900 }}>Pack Preview</div>
                <div className="muted" style={{ marginTop: 2 }}>
                  Rendering pages in the same ordering as export (Cover → TOC →
                  Sections)
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  className="btn"
                  onClick={downloadWord}
                  disabled={!computed}
                >
                  Download Word
                </button>
                <button
                  className="btn"
                  onClick={exportPackPDF}
                  disabled={!computed}
                >
                  Export PDF
                </button>
                <button className="btn primary" onClick={closePreview}>
                  Close
                </button>
              </div>
            </div>

            <iframe
              ref={previewIframeRef}
              title="Pack Preview"
              className="pack-preview-frame"
              srcDoc={previewHtml}
            />
          </div>
        </div>
      )}
    </div>
  );
}
